import React, { useState, useEffect, useCallback } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import * as XLSX from 'xlsx';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Alert,
  Chip,
  Box,
  AppBar,
  Toolbar,
  Grid,
  Card,
  CardContent,
  TablePagination,
  InputAdornment,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Checkbox,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ShoppingCart as OrdersIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Schedule as PendingIcon,
  Phone as PhoneIcon,
  Receipt as ReceiptIcon,
  Image as ImageIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Warning as WarningIcon,
  Inventory as InventoryIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  WhatsApp as WhatsAppIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  LocalShipping as LocalShippingIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import adminAuthService from '../services/adminAuthService';
import { API_BASE_URL } from '../config/api';

const FullControl = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const currentUser = adminAuthService.getCurrentUser();
  const isPackagingAdmin = currentUser?.role === 'packaging_admin';
  
  console.log('FullControl component mounted, user:', currentUser);
  
  // RTL theme configuration
  const rtlTheme = createTheme({
    direction: 'rtl',
    typography: {
      fontFamily: '"Cairo", "Arial", sans-serif'
    },
    components: {
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid #e0e7ff',
            overflow: 'hidden'
          }
        }
      },
      MuiTable: {
        styleOverrides: {
          root: {
            minWidth: '100%'
          }
        }
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: '#f8fafc',
            '& .MuiTableCell-head': {
              backgroundColor: '#f8fafc',
              fontWeight: 700,
              fontSize: '0.9rem',
              color: '#374151',
              borderBottom: '2px solid #e5e7eb',
              padding: '16px 12px'
            }
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            textAlign: 'right',
            padding: '12px',
            borderBottom: '1px solid #f1f5f9',
            fontSize: '0.875rem'
          },
          head: {
            backgroundColor: '#f8fafc',
            fontWeight: 700,
            fontSize: '0.9rem',
            color: '#374151',
            borderBottom: '2px solid #e5e7eb'
          }
        }
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: '#f8fafc',
              transition: 'background-color 0.2s ease'
            },
            '&:last-child .MuiTableCell-root': {
              borderBottom: 'none'
            }
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiInputBase-input': {
              textAlign: 'right'
            }
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            direction: 'rtl'
          }
        }
      }
    }
  });
  
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [bulkOrderIds, setBulkOrderIds] = useState('');
  const [bulkPhoneNumbers, setBulkPhoneNumbers] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [todayShippedFilter, setTodayShippedFilter] = useState(false);
  const [dateFilter, setDateFilter] = useState('');
  const [shippedDateFilter, setShippedDateFilter] = useState('');
  const [websiteOrdersOnly, setWebsiteOrdersOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editingItems, setEditingItems] = useState(false);
  const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [newProductQuantity, setNewProductQuantity] = useState(1);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Admin Notes state
  const [isEditingAdminNotes, setIsEditingAdminNotes] = useState(false);
  const [adminNotesValue, setAdminNotesValue] = useState('');
  const [savingAdminNotes, setSavingAdminNotes] = useState(false);

  // Phone editing state
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [phoneValue, setPhoneValue] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);

  // Prepared items state - track which items have been prepared per order
  const [preparedItemsByOrder, setPreparedItemsByOrder] = useState({});
  const [preparedItems, setPreparedItems] = useState(new Set());

  // CSV Export state
  const [selectedOrdersForExport, setSelectedOrdersForExport] = useState([]);
  const [selectAllOrders, setSelectAllOrders] = useState(false);

  // Missing Products state
  const [missingProducts, setMissingProducts] = useState([]);
  const [missingProductsDialogOpen, setMissingProductsDialogOpen] = useState(false);

  // WhatsApp messaging state
  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  const [selectedOrderForWhatsApp, setSelectedOrderForWhatsApp] = useState(null);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [bulkWhatsAppDialogOpen, setBulkWhatsAppDialogOpen] = useState(false);

  // Bulk status change state
  const [bulkStatusDialogOpen, setBulkStatusDialogOpen] = useState(false);
  const [selectedBulkStatus, setSelectedBulkStatus] = useState('');
  const [updatingBulkStatus, setUpdatingBulkStatus] = useState(false);

  // Create Order state
  const [createOrderDialogOpen, setCreateOrderDialogOpen] = useState(false);
  const [newOrder, setNewOrder] = useState({
    customer_name: '',
    customer_phone: '',
    city: '',
    shipping_address: '',
    notes: '',
    status: 'pending'
  });
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [showCustomCity, setShowCustomCity] = useState(false);
  const [customCity, setCustomCity] = useState('');

  // Shipping Integration state
  const [shippingDialogOpen, setShippingDialogOpen] = useState(false);
  const [shippingCities, setShippingCities] = useState([]);
  const [shippingRegions, setShippingRegions] = useState([]);
  const [packageSizes, setPackageSizes] = useState([]);
  const [selectedShippingCity, setSelectedShippingCity] = useState('');
  const [selectedShippingRegion, setSelectedShippingRegion] = useState('');
  const [selectedPackageSize, setSelectedPackageSize] = useState('');
  const [sendingToShipping, setSendingToShipping] = useState(false);
  const [syncingShippingStatus, setSyncingShippingStatus] = useState(false);
  const [loadingShippingData, setLoadingShippingData] = useState(false);
  
  // View Shipping Company Orders state
  const [shippingCompanyOrdersDialogOpen, setShippingCompanyOrdersDialogOpen] = useState(false);
  const [shippingCompanyOrders, setShippingCompanyOrders] = useState([]);
  const [loadingShippingOrders, setLoadingShippingOrders] = useState(false);

  // Statistics state
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    shipped: 0,
    delivered: 0,
    cancelled: 0,
    todayShipped: 0
  });

  const statusOptions = [
    { value: 'pending', label: 'في الانتظار', color: 'warning', icon: <PendingIcon /> },
    { value: 'confirmed', label: 'مؤكد', color: 'info', icon: <CheckIcon /> },
    { value: 'shipped', label: 'تم الشحن', color: 'primary', icon: <ShippingIcon /> },
    { value: 'delivered', label: 'تم التوصيل', color: 'success', icon: <CheckIcon /> },
    { value: 'cancelled', label: 'ملغي', color: 'error', icon: <CancelIcon /> }
  ];

  const cities = [
    'بغداد',
    'البصرة',
    'أربيل',
    'النجف',
    'كربلاء',
    'السليمانية',
    'الموصل',
    'دهوك',
    'بابل',
    'كركوك',
    'ذي قار',
    'المثنى',
    'القادسية',
    'ميسان',
    'واسط',
    'صلاح الدين',
    'ديالى',
    'الأنبار',
    'نينوى'
  ];

  // Function to validate phone number
  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^07\d{9}$/;
    return phoneRegex.test(phone);
  };

  // Helper function for status colors
  const getStatusColor = (colorType) => {
    const colors = {
      warning: '#f59e0b',
      info: '#3b82f6', 
      primary: '#6366f1',
      success: '#10b981',
      error: '#ef4444'
    };
    return colors[colorType] || '#6b7280';
  };

  // WhatsApp function
  const handleWhatsAppClick = (phoneNumber) => {
    if (!phoneNumber) return;
    
    // Clean phone number and format for WhatsApp
    let cleanNumber = phoneNumber.replace(/\D/g, '');
    
    // Add country code if not present (assuming Iraqi numbers)
    if (!cleanNumber.startsWith('964')) {
      if (cleanNumber.startsWith('0')) {
        cleanNumber = '964' + cleanNumber.substring(1);
      } else {
        cleanNumber = '964' + cleanNumber;
      }
    }
    
    const whatsappUrl = `https://wa.me/${cleanNumber}`;
    window.open(whatsappUrl, '_blank');
  };

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Loading orders for packaging...');
      const response = await fetch(`${API_BASE_URL}/api/admin_orders.php`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Orders response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Orders data received:', data);
      
      if (data.success && Array.isArray(data.orders)) {
        // Check first few orders for shipped_at field
        console.log('Sample order data:', data.orders.slice(0, 3));
        const shippedOrders = data.orders.filter(o => o.status === 'shipped');
        console.log('Shipped orders count:', shippedOrders.length);
        console.log('First shipped order:', shippedOrders[0]);
        
        // Show all orders for full control
        setOrders(data.orders);
        calculateStats(data.orders);
      } else {
        console.error('Invalid orders data structure:', data);
        setError('خطأ في تحميل بيانات الطلبات');
        setOrders([]);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
      setError('فشل في تحميل الطلبات: ' + error.message);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (adminAuthService.isAuthenticated()) {
      loadOrders();
    }
  }, [loadOrders]);

  const filterOrders = useCallback(() => {
    let filtered = orders;

    // Filter by bulk order IDs if provided (takes priority - ONLY show these orders)
    if (bulkOrderIds.trim()) {
      const orderIdList = bulkOrderIds
        .split(/[,\s]+/) // Split by comma or whitespace
        .map(id => id.trim())
        .filter(id => id.length > 0)
        .map(id => parseInt(id))
        .filter(id => !isNaN(id));
      
      if (orderIdList.length > 0) {
        filtered = filtered.filter(order => orderIdList.includes(order.id));
        // Sort by creation date and return immediately - ignore other filters
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setFilteredOrders(filtered);
        return;
      }
    }

    // Filter by bulk phone numbers if provided (respects other filters)
    if (bulkPhoneNumbers.trim()) {
      const phoneList = bulkPhoneNumbers
        .split(/[,\s]+/) // Split by comma or whitespace
        .map(phone => phone.trim())
        .filter(phone => phone.length > 0)
        .map(phone => phone.replace(/\D/g, '')); // Remove non-digits for comparison
      
      if (phoneList.length > 0) {
        filtered = filtered.filter(order => {
          if (!order.customer_phone) return false;
          const orderPhone = order.customer_phone.toString().replace(/\D/g, '');
          return phoneList.some(searchPhone => 
            orderPhone.includes(searchPhone) || searchPhone.includes(orderPhone)
          );
        });
        // Continue to apply other filters below
      }
    }

    // Regular search (only if no bulk IDs or bulk phones)
    if (searchQuery && !bulkOrderIds.trim() && !bulkPhoneNumbers.trim()) {
      filtered = filtered.filter(order =>
        order.id.toString().includes(searchQuery) ||
        (order.customer_name && order.customer_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (order.customer_email && order.customer_email.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (order.customer_phone && order.customer_phone.toString().includes(searchQuery))
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    // Filter for website orders only (exclude WhatsApp/Facebook guest orders)
    if (websiteOrdersOnly) {
      filtered = filtered.filter(order => order.user_id && order.user_id > 0);
    }

    if (todayShippedFilter) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      console.log('Today Shipped Filter Active');
      console.log('Today range:', today, 'to', tomorrow);
      
      filtered = filtered.filter(order => {
        if (!order.shipped_at || order.status !== 'shipped') {
          if (order.status === 'shipped' && !order.shipped_at) {
            console.log('Order', order.id, 'has status shipped but no shipped_at date');
          }
          return false;
        }
        const shippedDate = new Date(order.shipped_at);
        const isToday = shippedDate >= today && shippedDate < tomorrow;
        console.log('Order', order.id, '- shipped_at:', order.shipped_at, '- Date:', shippedDate, '- Is today:', isToday);
        return isToday;
      });
      
      console.log('Filtered orders count:', filtered.length);
    }

    // Filter by date range
    if (dateFilter) {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      let startDate, endDate;

      switch (dateFilter) {
        case 'today':
          startDate = new Date(today);
          endDate = new Date(today);
          endDate.setDate(endDate.getDate() + 1);
          break;
        case 'yesterday':
          startDate = new Date(today);
          startDate.setDate(startDate.getDate() - 1);
          endDate = new Date(today);
          break;
        case 'last7days':
          startDate = new Date(today);
          startDate.setDate(startDate.getDate() - 7);
          endDate = new Date(today);
          endDate.setDate(endDate.getDate() + 1);
          break;
        case 'last30days':
          startDate = new Date(today);
          startDate.setDate(startDate.getDate() - 30);
          endDate = new Date(today);
          endDate.setDate(endDate.getDate() + 1);
          break;
        case 'thisWeek':
          // Get the start of this week (Sunday)
          startDate = new Date(today);
          const dayOfWeek = startDate.getDay();
          startDate.setDate(startDate.getDate() - dayOfWeek);
          endDate = new Date(today);
          endDate.setDate(endDate.getDate() + 1);
          break;
        case 'lastWeek':
          // Get the start of last week (Sunday)
          startDate = new Date(today);
          const currentDayOfWeek = startDate.getDay();
          startDate.setDate(startDate.getDate() - currentDayOfWeek - 7);
          endDate = new Date(startDate);
          endDate.setDate(endDate.getDate() + 7);
          break;
        case 'thisMonth':
          startDate = new Date(today.getFullYear(), today.getMonth(), 1);
          endDate = new Date(today);
          endDate.setDate(endDate.getDate() + 1);
          break;
        case 'lastMonth':
          startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          endDate = new Date(today.getFullYear(), today.getMonth(), 1);
          break;
        default:
          break;
      }

      if (startDate && endDate) {
        filtered = filtered.filter(order => {
          const orderDate = new Date(order.created_at);
          return orderDate >= startDate && orderDate < endDate;
        });
      }
    }

    // Filter by specific shipped date
    if (shippedDateFilter) {
      const selectedDate = new Date(shippedDateFilter);
      selectedDate.setHours(0, 0, 0, 0);
      const nextDay = new Date(selectedDate);
      nextDay.setDate(nextDay.getDate() + 1);
      
      filtered = filtered.filter(order => {
        if (!order.shipped_at || order.status !== 'shipped') {
          return false;
        }
        const shippedDate = new Date(order.shipped_at);
        return shippedDate >= selectedDate && shippedDate < nextDay;
      });
    }

    // Sort by creation date (newest first)
    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    setFilteredOrders(filtered);
  }, [orders, searchQuery, bulkPhoneNumbers, statusFilter, todayShippedFilter, dateFilter, shippedDateFilter, websiteOrdersOnly]);

  useEffect(() => {
    filterOrders();
  }, [orders, searchQuery, bulkOrderIds, bulkPhoneNumbers, statusFilter, todayShippedFilter, dateFilter, shippedDateFilter, websiteOrdersOnly, filterOrders]);

  const calculateStats = (ordersList) => {
    // Calculate today's date range
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const stats = {
      total: ordersList.length,
      pending: ordersList.filter(order => order.status === 'pending').length,
      confirmed: ordersList.filter(order => order.status === 'confirmed').length,
      shipped: ordersList.filter(order => order.status === 'shipped').length,
      delivered: ordersList.filter(order => order.status === 'delivered').length,
      cancelled: ordersList.filter(order => order.status === 'cancelled').length,
      todayShipped: ordersList.filter(order => {
        if (!order.shipped_at || order.status !== 'shipped') return false;
        const shippedDate = new Date(order.shipped_at);
        return shippedDate >= today && shippedDate < tomorrow;
      }).length
    };
    setStats(stats);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value);
    setPage(0);
  };

  const getStatusInfo = (status) => {
    return statusOptions.find(s => s.value === status) || statusOptions[0];
  };

  // Handle status change
  const handleStatusChange = async (orderId, newStatusValue) => {
    try {
      console.log(`Changing order ${orderId} status to ${newStatusValue}`);
      
      // Prepare the request body
      const requestBody = {
        action: 'update_status',
        order_id: orderId,
        status: newStatusValue
      };

      // If status is being changed to "shipped", include the current timestamp
      if (newStatusValue === 'shipped') {
        requestBody.shipped_at = new Date().toISOString();
      }
      
      const response = await fetch(`${API_BASE_URL}/api/admin_orders.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      console.log('Status change response:', data);

      if (data.success) {
        // Update local state
        const updatedOrders = orders.map(order => {
          if (order.id === orderId) {
            const updatedOrder = { ...order, status: newStatusValue };
            // Add shipped_at timestamp if status is shipped
            if (newStatusValue === 'shipped') {
              updatedOrder.shipped_at = new Date().toISOString();
            }
            return updatedOrder;
          }
          return order;
        });
        setOrders(updatedOrders);
        calculateStats(updatedOrders);
        
        // Update selectedOrder if it's the one being changed
        if (selectedOrder && selectedOrder.id === orderId) {
          const updatedSelectedOrder = { ...selectedOrder, status: newStatusValue };
          if (newStatusValue === 'shipped') {
            updatedSelectedOrder.shipped_at = new Date().toISOString();
          }
          setSelectedOrder(updatedSelectedOrder);
        }
        
        setSuccess(`تم تحديث حالة الطلب #${orderId} بنجاح إلى "${statusOptions.find(s => s.value === newStatusValue)?.label}"`);
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'فشل تحديث حالة الطلب');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      setError('حدث خطأ أثناء تحديث حالة الطلب: ' + error.message);
    }
  };

  // Load available products for adding to order
  const loadAvailableProducts = async (searchQuery = '') => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setAvailableProducts([]);
      return;
    }

    try {
      setLoadingProducts(true);
      console.log('Searching products:', searchQuery);
      
      const response = await fetch(`${API_BASE_URL}/api/admin_products.php?search=${encodeURIComponent(searchQuery)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json',
        }
      });

      console.log('Products API response:', response.status);
      const data = await response.json();
      console.log('Products data:', data);
      
      if (response.ok && data.success) {
        setAvailableProducts(data.products || []);
        console.log('Loaded products:', data.products?.length || 0);
      } else {
        console.error('Failed to load products:', data.message);
        setError(data.message || 'فشل في تحميل المنتجات');
      }
    } catch (error) {
      console.error('Error loading products:', error);
      setError('حدث خطأ في تحميل المنتجات');
    } finally {
      setLoadingProducts(false);
    }
  };

  // Debounce search
  useEffect(() => {
    if (!addProductDialogOpen) return;

    const timeoutId = setTimeout(() => {
      loadAvailableProducts(productSearchQuery);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [productSearchQuery, addProductDialogOpen]);

  // Load missing products on mount
  useEffect(() => {
    loadMissingProducts();
  }, []);

  const handleOpenAddProductDialog = () => {
    setAddProductDialogOpen(true);
    setSelectedProduct(null);
    setNewProductQuantity(1);
    setProductSearchQuery('');
    setAvailableProducts([]); // Clear products when opening
  };

  // Function to recalculate order totals based on items with weight-based delivery
  // Function to round delivery price DOWN to nearest 250 increment
  // Examples: 5001-5249 → 5000, 5250-5499 → 5250, 5500-5749 → 5500, 5750-5999 → 5750
  const roundDeliveryPrice = (price) => {
    return Math.floor(price / 250) * 250;
  };

  const recalculateOrderTotals = (items) => {
    if (!items || items.length === 0) {
      return {
        subtotal: 0,
        delivery: 5000, // Minimum delivery
        total: 5000
      };
    }

    // Calculate subtotal and total weight
    let subtotal = 0;
    let totalWeight = 0;

    items.forEach(item => {
      const quantity = parseFloat(item.quantity || 0);
      const price = parseFloat(item.price || 0);
      const weight = parseFloat(item.weight || 0);
      
      subtotal += quantity * price;
      totalWeight += weight * quantity;
    });

    // Calculate delivery cost based on weight
    // Minimum delivery cost is 5000 IQD
    let delivery = 5000;
    
    // If weight exceeds 5kg, add 1000 IQD per additional kg
    if (totalWeight > 5) {
      const additionalWeight = totalWeight - 5;
      delivery += additionalWeight * 1000;
    }

    // Round delivery price DOWN to nearest 250 increment
    delivery = roundDeliveryPrice(delivery);

    const total = subtotal + delivery;

    return {
      subtotal,
      delivery,
      total
    };
  };

  const handleAddProductToOrder = () => {
    if (!selectedProduct || !selectedOrder) return;

    const finalPrice = selectedProduct.after_discount || selectedProduct.sell_price || selectedProduct.price || 0;

    const newItem = {
      id: `new_${Date.now()}`,
      order_item_id: null,
      is_new: true,
      product_id: selectedProduct.id,
      product_name: selectedProduct.name,
      product_sku: selectedProduct.sku || '',
      brand_name: selectedProduct.brand_name || '',
      quantity: newProductQuantity,
      price: finalPrice,
      weight: selectedProduct.weight || 0, // Include weight from product
      product_image: selectedProduct.image_url || selectedProduct.image || ''
    };

    const updatedItems = [...(selectedOrder.items || []), newItem];
    
    // Recalculate totals with weight-based delivery
    const totals = recalculateOrderTotals(updatedItems);
    
    // Preserve discount from database
    const discountAmount = parseFloat(selectedOrder.discount_amount || 0);
    
    setSelectedOrder({
      ...selectedOrder,
      items: updatedItems,
      subtotal: totals.subtotal,
      delivery_fee: totals.delivery,
      discount_amount: discountAmount,
      total_amount: totals.subtotal + totals.delivery - discountAmount
    });
    
    setAddProductDialogOpen(false);
    setSuccess(`تم إضافة ${selectedProduct.name} إلى الطلب`);
    setTimeout(() => setSuccess(''), 3000);
  };

  const getFilteredProducts = () => {
    if (!productSearchQuery) return availableProducts;
    
    return availableProducts.filter(product =>
      product.name?.toLowerCase().includes(productSearchQuery.toLowerCase()) ||
      (product.sku && product.sku.toLowerCase().includes(productSearchQuery.toLowerCase())) ||
      (product.barcode && product.barcode.toLowerCase().includes(productSearchQuery.toLowerCase())) ||
      (product.brand_name && product.brand_name.toLowerCase().includes(productSearchQuery.toLowerCase()))
    );
  };

  // Handle item quantity change
  const handleQuantityChange = (itemId, delta) => {
    setSelectedOrder(prev => {
      const updatedItems = prev.items.map(item => {
        if (item.id === itemId) {
          const newQuantity = Math.max(1, parseInt(item.quantity) + delta);
          return { ...item, quantity: newQuantity };
        }
        return item;
      });
      
      // Recalculate totals with weight-based delivery
      const totals = recalculateOrderTotals(updatedItems);
      
      // Preserve discount from database
      const discountAmount = parseFloat(prev.discount_amount || 0);
      
      return {
        ...prev,
        items: updatedItems,
        subtotal: totals.subtotal,
        delivery_fee: totals.delivery,
        discount_amount: discountAmount,
        total_amount: totals.subtotal + totals.delivery - discountAmount
      };
    });
  };

  // Handle item deletion
  const handleDeleteItem = (itemId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المنتج؟')) return;
    
    setSelectedOrder(prev => {
      const updatedItems = prev.items.filter(item => item.id !== itemId);
      
      // Recalculate totals with weight-based delivery
      const totals = recalculateOrderTotals(updatedItems);
      
      // Preserve discount from database
      const discountAmount = parseFloat(prev.discount_amount || 0);
      
      return {
        ...prev,
        items: updatedItems,
        subtotal: totals.subtotal,
        delivery_fee: totals.delivery,
        discount_amount: discountAmount,
        total_amount: totals.subtotal + totals.delivery - discountAmount
      };
    });
  };

  // Handle save order changes
  const handleSaveOrderChanges = async () => {
    try {
      // Prepare items data
      const itemsData = selectedOrder.items.map(item => ({
        order_item_id: item.order_item_id || item.id,
        product_id: item.product_id,
        product_name: item.product_name,
        product_sku: item.product_sku || '',
        brand_name: item.brand_name || '',
        quantity: parseInt(item.quantity) || 0,
        price: parseFloat(item.price) || 0
      }));

      const orderData = {
        action: 'update_order',
        order_id: selectedOrder.id,
        customer_name: selectedOrder.customer_name,
        customer_phone: selectedOrder.customer_phone,
        customer_email: selectedOrder.customer_email,
        shipping_address: selectedOrder.shipping_address,
        city: selectedOrder.city,
        status: selectedOrder.status,
        notes: selectedOrder.notes,
        total_amount: selectedOrder.total_amount,
        delivery: selectedOrder.delivery_fee,
        items: itemsData,
        update_items: true,
        recalculate_totals: true,
        items_changed: true
      };

      console.log('Saving order changes:', orderData);

      const response = await fetch(`${API_BASE_URL}/api/admin_orders.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });

      console.log('Response status:', response.status);
      const data = await response.json();
      console.log('Response data:', data);
      
      if (data.success) {
        setSuccess('تم حفظ التغييرات بنجاح');
        setTimeout(() => setSuccess(''), 3000);
        setEditingItems(false);
        
        // Reload order details
        await handleViewOrderDetails(selectedOrder);
        loadOrders();
      } else {
        setError(data.message || 'فشل في حفظ التغييرات');
      }
    } catch (error) {
      console.error('Error saving order changes:', error);
      setError('حدث خطأ أثناء حفظ التغييرات: ' + error.message);
    }
  };

  // Function to save admin notes
  const handleSaveAdminNotes = async () => {
    if (!selectedOrder) return;
    
    setSavingAdminNotes(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin_orders.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'update_order',
          order_id: selectedOrder.id,
          admin_notes: adminNotesValue
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess('تم حفظ الملاحظات بنجاح');
        
        // Update selected order
        setSelectedOrder(prev => ({
          ...prev,
          admin_notes: adminNotesValue
        }));
        
        // Update orders list
        setOrders(prevOrders => 
          prevOrders.map(order => 
            order.id === selectedOrder.id 
              ? { ...order, admin_notes: adminNotesValue }
              : order
          )
        );
        
        setIsEditingAdminNotes(false);
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'فشل في حفظ الملاحظات');
      }
    } catch (error) {
      console.error('Error saving admin notes:', error);
      setError('فشل في حفظ الملاحظات: ' + error.message);
    } finally {
      setSavingAdminNotes(false);
    }
  };

  // Function to save phone number
  const handleSavePhone = async () => {
    if (!selectedOrder) return;
    
    // Validate phone number
    if (!phoneValue || phoneValue.trim() === '') {
      setError('رجاء إدخال رقم هاتف صحيح');
      return;
    }
    
    setSavingPhone(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin_orders.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'update_order',
          order_id: selectedOrder.id,
          customer_phone: phoneValue
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess('تم حفظ رقم الهاتف بنجاح');
        
        // Update selected order
        setSelectedOrder(prev => ({
          ...prev,
          customer_phone: phoneValue
        }));
        
        // Update orders list
        setOrders(prevOrders => 
          prevOrders.map(order => 
            order.id === selectedOrder.id 
              ? { ...order, customer_phone: phoneValue }
              : order
          )
        );
        
        setIsEditingPhone(false);
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'فشل في حفظ رقم الهاتف');
      }
    } catch (error) {
      console.error('Error saving phone:', error);
      setError('فشل في حفظ رقم الهاتف: ' + error.message);
    } finally {
      setSavingPhone(false);
    }
  };

  // Handle toggling item prepared status
  const handleToggleItemPrepared = (itemId) => {
    setPreparedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      
      // Save to persistent storage by order ID
      if (selectedOrder) {
        setPreparedItemsByOrder(prevOrders => ({
          ...prevOrders,
          [selectedOrder.id]: newSet
        }));
      }
      
      return newSet;
    });
  };

  // Load missing products from database
  const loadMissingProducts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/missing_products.php`, {
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`
        }
      });
      const data = await response.json();
      if (data.success) {
        // Convert database format to local format
        const formattedProducts = data.data.map(p => ({
          id: p.id,
          orderId: p.order_id,
          productId: p.product_id,
          productName: p.product_name,
          sku: p.sku || '',
          brandName: p.brand_name || '',
          image: p.image || '',
          quantity: p.quantity,
          addedAt: p.added_at
        }));
        setMissingProducts(formattedProducts);
      }
    } catch (error) {
      console.error('Error loading missing products:', error);
    }
  };

  // Handle marking product as missing
  const handleMarkProductAsMissing = async (product, orderId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/missing_products.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          orderId: orderId,
          productId: product.product_id || product.id,
          productName: product.product_name,
          sku: product.product_sku || '',
          brandName: product.brand_name || '',
          image: product.product_image || '',
          quantity: product.quantity,
          addedAt: new Date().toISOString()
        })
      });

      const data = await response.json();
      if (data.success) {
        loadMissingProducts(); // Reload the list
        setSuccess(data.message);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'فشل في إضافة المنتج');
        setTimeout(() => setError(''), 3000);
      }
    } catch (error) {
      console.error('Error marking product as missing:', error);
      setError('حدث خطأ في إضافة المنتج');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Handle removing product from missing list
  const handleRemoveMissingProduct = async (missingProductId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/missing_products.php`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id: missingProductId })
      });

      const data = await response.json();
      if (data.success) {
        loadMissingProducts(); // Reload the list
        setSuccess(data.message);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'فشل في حذف المنتج');
        setTimeout(() => setError(''), 3000);
      }
    } catch (error) {
      console.error('Error removing missing product:', error);
      setError('حدث خطأ في حذف المنتج');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Clear all missing products
  const handleClearMissingProducts = async () => {
    if (window.confirm('هل أنت متأكد من حذف جميع المنتجات المفقودة؟')) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/missing_products.php`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${adminAuthService.getToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ action: 'clear_all' })
        });

        const data = await response.json();
        if (data.success) {
          setMissingProducts([]);
          setSuccess(data.message);
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError(data.message || 'فشل في مسح القائمة');
          setTimeout(() => setError(''), 3000);
        }
      } catch (error) {
        console.error('Error clearing missing products:', error);
        setError('حدث خطأ في مسح القائمة');
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  // Export missing products to Excel
  const exportMissingProductsToExcel = () => {
    if (missingProducts.length === 0) {
      setError('لا توجد منتجات مفقودة للتصدير');
      return;
    }

    const rows = missingProducts.map((product, index) => ({
      '#': index + 1,
      'اسم المنتج': product.productName,
      'SKU': product.sku || '-',
      'العلامة التجارية': product.brandName || '-',
      'الكمية': product.quantity,
      'رقم الطلب': product.orderId,
      'تاريخ الإضافة': new Date(product.addedAt).toLocaleDateString('ar-EG')
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const columnWidths = [
      { wch: 5 },  // #
      { wch: 30 }, // اسم المنتج
      { wch: 15 }, // SKU
      { wch: 20 }, // العلامة التجارية
      { wch: 10 }, // الكمية
      { wch: 12 }, // رقم الطلب
      { wch: 15 }  // تاريخ الإضافة
    ];
    worksheet['!cols'] = columnWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Missing Products');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `missing_products_${new Date().toISOString().slice(0, 10)}.xlsx`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setSuccess(`تم تصدير ${missingProducts.length} منتج مفقود`);
  };

  // Handle order selection for export
  const handleSelectOrderForExport = (orderId) => {
    if (selectedOrdersForExport.includes(orderId)) {
      setSelectedOrdersForExport(selectedOrdersForExport.filter(id => id !== orderId));
    } else {
      setSelectedOrdersForExport([...selectedOrdersForExport, orderId]);
    }
  };

  const handleSelectAllOrdersForExport = () => {
    if (selectAllOrders) {
      setSelectedOrdersForExport([]);
      setSelectAllOrders(false);
    } else {
      const currentPageOrders = rowsPerPage > 0
        ? filteredOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        : filteredOrders;
      const currentPageOrderIds = currentPageOrders.map(order => order.id);
      setSelectedOrdersForExport(currentPageOrderIds);
      setSelectAllOrders(true);
    }
  };

  const formatPriceValue = (value) => {
    const numericValue = parseFloat(value || 0);
    return numericValue.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const escapeHtml = (value) => {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const handleCheckPrice = async () => {
    if (selectedOrdersForExport.length === 0) {
      setError('الرجاء تحديد طلبات لعرض الأسعار');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const orderDetailsPromises = selectedOrdersForExport.map(async (orderId) => {
        const response = await fetch(`${API_BASE_URL}/api/admin_orders.php?order_id=${orderId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${adminAuthService.getToken()}`,
            'Content-Type': 'application/json'
          }
        });

        const data = await response.json();
        return data.success ? data.order : null;
      });

      const ordersWithDetails = await Promise.all(orderDetailsPromises);
      const validOrders = ordersWithDetails.filter(order => order !== null);
      const itemRows = validOrders.flatMap(order =>
        (order.items || []).map(item => ({
          orderId: order.id,
          name: item.product_name || '-',
          sku: item.product_sku || '-',
          brand: item.brand_name || '-',
          purchasePrice: item.purchase_price || 0,
          salePrice: item.price || 0,
          quantity: item.quantity || 0
        }))
      );

      if (itemRows.length === 0) {
        setError('لا توجد منتجات في الطلبات المحددة');
        return;
      }

      const priceWindow = window.open('', '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');

      if (!priceWindow) {
        setError('تعذر فتح نافذة جديدة. تأكد من السماح بالنوافذ المنبثقة.');
        return;
      }

      const rowsHtml = itemRows.map((item, index) => `
        <tr>
          <td>${index + 1}</td>
          <td>#${escapeHtml(item.orderId)}</td>
          <td>${escapeHtml(item.name)}</td>
          <td>${escapeHtml(item.sku)}</td>
          <td>${escapeHtml(item.brand)}</td>
          <td>${formatPriceValue(item.purchasePrice)} IQD</td>
          <td>${formatPriceValue(item.salePrice)} IQD</td>
          <td>${escapeHtml(item.quantity)}</td>
        </tr>
      `).join('');

      priceWindow.document.write(`
        <!DOCTYPE html>
        <html lang="ar" dir="rtl">
          <head>
            <meta charset="UTF-8" />
            <title>Check Price</title>
            <style>
              body {
                font-family: Cairo, Arial, sans-serif;
                margin: 24px;
                background: #f8fafc;
                color: #1f2937;
              }
              h1 {
                margin: 0 0 8px;
                color: #10b981;
              }
              .summary {
                margin-bottom: 20px;
                font-size: 14px;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                background: #ffffff;
                box-shadow: 0 4px 20px rgba(15, 23, 42, 0.08);
              }
              th, td {
                border: 1px solid #e5e7eb;
                padding: 12px;
                text-align: right;
                font-size: 14px;
              }
              th {
                background: #ecfdf5;
                font-weight: 700;
              }
              tr:nth-child(even) {
                background: #f9fafb;
              }
            </style>
          </head>
          <body>
            <h1>Check Price</h1>
            <div class="summary">عدد الطلبات: ${validOrders.length} | عدد المنتجات: ${itemRows.length}</div>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>رقم الطلب</th>
                  <th>اسم المنتج</th>
                  <th>SKU</th>
                  <th>البراند</th>
                  <th>سعر الشراء</th>
                  <th>سعر البيع للحبة</th>
                  <th>الكمية</th>
                </tr>
              </thead>
              <tbody>${rowsHtml}</tbody>
            </table>
          </body>
        </html>
      `);
      priceWindow.document.close();

      setSuccess(`تم فتح نافذة الأسعار لـ ${itemRows.length} منتج`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error checking prices:', error);
      setError('حدث خطأ أثناء عرض الأسعار: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Generate CSV for delivery company
  const generateDeliveryCSV = async () => {
    if (selectedOrdersForExport.length === 0) {
      setError('الرجاء تحديد طلبات للتصدير');
      return;
    }

    try {
      setLoading(true);
      
      // Fetch full details for all selected orders
      const orderDetailsPromises = selectedOrdersForExport.map(async (orderId) => {
        const response = await fetch(`${API_BASE_URL}/api/admin_orders.php?order_id=${orderId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${adminAuthService.getToken()}`,
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();
        return data.success ? data.order : null;
      });

      const ordersWithDetails = await Promise.all(orderDetailsPromises);
      const validOrders = ordersWithDetails.filter(order => order !== null);

      // Generate Excel rows with proper data
      const rows = validOrders.map(order => {
        const totalQuantity = order.items?.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0) || 0;
        
        // Calculate total price: products + delivery - discounts
        const subtotal = order.items?.reduce((sum, item) => {
          return sum + (parseFloat(item.price || 0) * parseFloat(item.quantity || 0));
        }, 0) || parseFloat(order.subtotal || 0) || 0;
        
        const deliveryFee = parseFloat(order.delivery || order.delivery_fee || 5000);
        const discountAmount = parseFloat(order.discount_amount || 0);
        const totalPrice = subtotal + deliveryFee - discountAmount;
        
        const exportDate = new Date().toLocaleDateString('ar-EG');
        
        // Order size is always normal
        const orderSize = 'عادي';

        return {
          'اسم الزبون': order.order_id || order.id || '',
          'رقم الهاتف الاساسي': order.customer_phone || '',
          'رقم الهاتف الثانوي': '', // Secondary phone (not available)
          'المحافظة': order.city || '',
          'المنطقة': order.shipping_address || '',
          'نوع البضاعه': 'عدد يدوية', // Product type
          'عدد القطع': totalQuantity,
          'السعر مع التوصيل': totalPrice,
          'حجم الطلب': orderSize,
          'الملاحظات': `تاريخ التصدير: ${exportDate}${order.admin_notes ? ' - ' + order.admin_notes : ''}`,
          'نوع الطلب': 'طلب جديد' // Order type
        };
      });

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(rows);
      
      // Set column widths for better readability
      const columnWidths = [
        { wch: 20 }, // اسم الزبون
        { wch: 15 }, // رقم الهاتف الاساسي
        { wch: 15 }, // رقم الهاتف الثانوي
        { wch: 15 }, // المحافظة
        { wch: 30 }, // المنطقة
        { wch: 15 }, // نوع البضاعه
        { wch: 12 }, // عدد القطع
        { wch: 15 }, // السعر مع التوصيل
        { wch: 12 }, // حجم الطلب
        { wch: 30 }, // الملاحظات
        { wch: 15 }  // نوع الطلب
      ];
      worksheet['!cols'] = columnWidths;

      // Set RTL for the worksheet
      if (!worksheet['!cols']) worksheet['!cols'] = [];
      worksheet['!dir'] = 'rtl';

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Delivery Orders');

      // Generate Excel file and download
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `delivery_orders_${new Date().toISOString().slice(0, 10)}.xlsx`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSuccess(`تم تصدير ${validOrders.length} طلب`);
      setSelectedOrdersForExport([]);
      setSelectAllOrders(false);
      setLoading(false);
    } catch (error) {
      console.error('Error generating CSV:', error);
      setError('حدث خطأ أثناء تصدير الطلبات');
      setLoading(false);
    }
  };

  // Generate Stock Update Excel for delivery company
  const generateStockExport = async () => {
    if (selectedOrdersForExport.length === 0) {
      setError('الرجاء تحديد طلبات للتصدير');
      return;
    }

    try {
      setLoading(true);
      
      // Fetch full details for all selected orders
      const orderDetailsPromises = selectedOrdersForExport.map(async (orderId) => {
        const response = await fetch(`${API_BASE_URL}/api/admin_orders.php?order_id=${orderId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${adminAuthService.getToken()}`,
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();
        return data.success ? data.order : null;
      });

      const ordersWithDetails = await Promise.all(orderDetailsPromises);
      const validOrders = ordersWithDetails.filter(order => order !== null);

      // Aggregate all items from selected orders
      const allItems = [];
      const exportDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

      validOrders.forEach(order => {
        if (order.items && Array.isArray(order.items)) {
          order.items.forEach(item => {
            allItems.push({
              'Invoice No.': '',
              'Customer name': 'online',
              'Customer Phone number': '7813438118',
              'Customer Email': 'alatharibaq@gmail.com',
              'Sale Date': exportDate,
              'Product name': item.product_name || '',
              'Product SKU': item.product_sku || '',
              'Quantity': parseInt(item.quantity) || 0,
              'Product Unit': 'قطعة',
              'Unit Price': parseFloat(item.purchase_price || 0),
              'Item Tax': '',
              'Item Discount': '',
              'Item Description': '',
              'Types of service': '',
              'Custom Field 1': '',
              'Custom Field 2': '',
              'Custom Field 3': '',
              'Custom Field 4': ''
            });
          });
        }
      });

      if (allItems.length === 0) {
        setError('لا توجد منتجات في الطلبات المحددة');
        setLoading(false);
        return;
      }

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(allItems);
      
      // Set column widths
      const columnWidths = [
        { wch: 12 }, // Invoice No.
        { wch: 15 }, // Customer name
        { wch: 18 }, // Customer Phone number
        { wch: 25 }, // Customer Email
        { wch: 12 }, // Sale Date
        { wch: 30 }, // Product name
        { wch: 15 }, // Product SKU
        { wch: 10 }, // Quantity
        { wch: 12 }, // Product Unit
        { wch: 12 }, // Unit Price
        { wch: 12 }, // Item Tax
        { wch: 12 }, // Item Discount
        { wch: 20 }, // Item Description
        { wch: 15 }, // Types of service
        { wch: 15 }, // Custom Field 1
        { wch: 15 }, // Custom Field 2
        { wch: 15 }, // Custom Field 3
        { wch: 15 }  // Custom Field 4
      ];
      worksheet['!cols'] = columnWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Stock Update');

      // Generate Excel file and download
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `stock_update_${new Date().toISOString().slice(0, 10)}.xlsx`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSuccess(`تم تصدير ${allItems.length} منتج من ${validOrders.length} طلب`);
      setSelectedOrdersForExport([]);
      setSelectAllOrders(false);
      setLoading(false);
    } catch (error) {
      console.error('Error generating stock export:', error);
      setError('حدث خطأ أثناء تصدير المخزون');
      setLoading(false);
    }
  };

  // Handle view order details
  const handleViewOrderDetails = async (order) => {
    try {
      console.log('Requesting details for order:', order.id);
      
      const apiUrl = `${API_BASE_URL}/api/admin_orders.php?order_id=${order.id}&_t=${Date.now()}`;
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      console.log('Order details data:', data);
      
      if (response.ok && data.success) {
        const orderData = data.order;
        
        // Normalize items structure
        if (orderData.items) {
          orderData.items = orderData.items.map(item => ({
            ...item,
            id: item.order_item_id,
            order_item_id: item.order_item_id
          }));
        }
        
        // Preserve discount information from backend
        orderData.discount_amount = parseFloat(orderData.discount_amount || 0);
        orderData.coupon_code = orderData.coupon_code || '';
        
        // Preserve delivery price from database on initial load
        // When items are edited, the delivery will be recalculated using the formula
        if (orderData.items && orderData.items.length > 0) {
          // Calculate subtotal from items
          const subtotal = orderData.items.reduce((sum, item) => {
            return sum + (parseFloat(item.price || 0) * parseFloat(item.quantity || 0));
          }, 0);
          
          orderData.subtotal = subtotal;
          // Use delivery price from database
          orderData.delivery_fee = parseFloat(orderData.delivery || orderData.delivery_fee || 5000);
          // Calculate total
          orderData.total_amount = subtotal + orderData.delivery_fee - orderData.discount_amount;
        } else {
          // Fallback if no items
          orderData.subtotal = orderData.subtotal || 0;
          orderData.delivery_fee = orderData.delivery || orderData.delivery_fee || 5000;
          orderData.total_amount = orderData.total_price || orderData.total || orderData.total_amount || 0;
        }
        
        console.log('Order details loaded:', orderData);
        console.log('Subtotal:', orderData.subtotal, 'Delivery:', orderData.delivery_fee, 'Total:', orderData.total_amount);
        setSelectedOrder(orderData);
        
        // Initialize admin notes
        setAdminNotesValue(orderData.admin_notes || '');
        setIsEditingAdminNotes(false);
        
        // Initialize prepared items - restore from storage if exists, otherwise start with empty set
        const savedPreparedItems = preparedItemsByOrder[orderData.id];
        setPreparedItems(savedPreparedItems ? new Set(savedPreparedItems) : new Set());
        
        setDetailsDialogOpen(true);
      } else {
        setError(data.message || 'فشل في تحميل تفاصيل الطلب');
      }
    } catch (error) {
      console.error('ViewOrderDetails error:', error);
      setError('حدث خطأ في الاتصال بالخادم');
    }
  };

  // Handle sending WhatsApp message
  const handleSendWhatsAppMessage = async () => {
    if (!selectedOrderForWhatsApp) return;
    
    setSendingWhatsApp(true);
    try {
      console.log('Sending WhatsApp message for order:', selectedOrderForWhatsApp);
      console.log('Token:', adminAuthService.getToken());
      
      const response = await fetch(`${API_BASE_URL}/api/whatsapp_order_message.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'send_delivery_update',
          order_id: selectedOrderForWhatsApp.id,
          phone: selectedOrderForWhatsApp.customer_phone
        })
      });

      console.log('WhatsApp response status:', response.status);
      const data = await response.json();
      console.log('WhatsApp response data:', data);
      
      if (data.success) {
        setSuccess('تم إرسال رسالة واتساب بنجاح إلى ' + selectedOrderForWhatsApp.customer_name);
        setWhatsappDialogOpen(false);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'فشل في إرسال الرسالة');
        setTimeout(() => setError(''), 5000);
      }
    } catch (error) {
      console.error('Error sending WhatsApp message:', error);
      setError('حدث خطأ في إرسال الرسالة: ' + error.message);
      setTimeout(() => setError(''), 5000);
    } finally {
      setSendingWhatsApp(false);
    }
  };

  // Handle bulk WhatsApp sending
  const handleBulkWhatsAppSend = async () => {
    if (selectedOrdersForExport.length === 0) return;
    
    setSendingWhatsApp(true);
    let successCount = 0;
    let failCount = 0;
    
    try {
      for (const orderId of selectedOrdersForExport) {
        const order = orders.find(o => o.id === orderId);
        if (!order || !order.customer_phone) {
          failCount++;
          continue;
        }
        
        try {
          const response = await fetch(`${API_BASE_URL}/api/whatsapp_order_message.php`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${adminAuthService.getToken()}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              action: 'send_delivery_update',
              order_id: order.id,
              phone: order.customer_phone
            })
          });
          
          const data = await response.json();
          if (data.success) {
            successCount++;
          } else {
            failCount++;
          }
          
          // Small delay between requests to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 500));
        } catch (error) {
          failCount++;
          console.error('Error sending to order:', orderId, error);
        }
      }
      
      // Show results
      if (successCount > 0 && failCount === 0) {
        setSuccess(`تم إرسال ${successCount} رسالة واتساب بنجاح`);
      } else if (successCount > 0 && failCount > 0) {
        setSuccess(`تم إرسال ${successCount} رسالة بنجاح، فشل ${failCount}`);
      } else {
        setError(`فشل إرسال جميع الرسائل (${failCount})`);
      }
      
      setBulkWhatsAppDialogOpen(false);
      setSelectedOrdersForExport([]);
      setSelectAllOrders(false);
      setTimeout(() => { setSuccess(''); setError(''); }, 5000);
      
    } finally {
      setSendingWhatsApp(false);
    }
  };

  // Function to create new order
  const handleCreateOrder = async () => {
    setCreatingOrder(true);
    try {
      // Validate required fields
      if (!newOrder.customer_name || !newOrder.customer_phone || !newOrder.city || !newOrder.shipping_address) {
        setError('الرجاء ملء جميع الحقول المطلوبة');
        setCreatingOrder(false);
        return;
      }

      // Validate phone format
      if (!validatePhoneNumber(newOrder.customer_phone)) {
        setError('رقم الهاتف يجب أن يكون بصيغة 07XXXXXXXXX (11 رقم)');
        setPhoneError('رقم الهاتف يجب أن يبدأ بـ 07 ويتكون من 11 رقم');
        setCreatingOrder(false);
        return;
      }

      // Use custom city if "أخرى" is selected
      const finalCity = newOrder.city === 'أخرى' ? customCity : newOrder.city;
      
      if (newOrder.city === 'أخرى' && !customCity.trim()) {
        setError('الرجاء إدخال اسم المدينة');
        setCreatingOrder(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/admin_orders.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'create_order',
          ...newOrder,
          city: finalCity
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess('تم إنشاء الطلب بنجاح');
        setCreateOrderDialogOpen(false);
        
        // Reset form
        setNewOrder({
          customer_name: '',
          customer_phone: '',
          city: '',
          shipping_address: '',
          notes: '',
          status: 'pending'
        });
        setShowCustomCity(false);
        setCustomCity('');
        setPhoneError('');
        
        // Reload orders
        await loadOrders();
        
        // Automatically open the order details to add products
        if (data.data && data.data.order_id) {
          // Fetch the newly created order details
          const orderResponse = await fetch(`${API_BASE_URL}/api/admin_orders.php?order_id=${data.data.order_id}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${adminAuthService.getToken()}`,
              'Content-Type': 'application/json'
            }
          });
          
          const orderData = await orderResponse.json();
          
          if (orderData.success && orderData.order) {
            // Open order details dialog
            await handleViewOrderDetails(orderData.order);
          }
        }
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'فشل في إنشاء الطلب');
      }
    } catch (error) {
      console.error('Error creating order:', error);
      setError('فشل في إنشاء الطلب: ' + error.message);
    } finally {
      setCreatingOrder(false);
    }
  };

  // Handle bulk status change
  const handleBulkStatusChange = async () => {
    if (!selectedBulkStatus || selectedOrdersForExport.length === 0) {
      setError('الرجاء تحديد الحالة والطلبات');
      return;
    }

    setUpdatingBulkStatus(true);
    try {
      const updatePromises = selectedOrdersForExport.map(async (orderId) => {
        const requestBody = {
          action: 'update_status',
          order_id: orderId,
          status: selectedBulkStatus
        };

        // If status is being changed to "shipped", include the current timestamp
        if (selectedBulkStatus === 'shipped') {
          requestBody.shipped_at = new Date().toISOString();
        }

        const response = await fetch(`${API_BASE_URL}/api/admin_orders.php`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminAuthService.getToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });

        const data = await response.json();
        return { orderId, success: data.success };
      });

      const results = await Promise.all(updatePromises);
      const successCount = results.filter(r => r.success).length;

      if (successCount > 0) {
        setSuccess(`تم تحديث حالة ${successCount} طلب بنجاح`);
        
        // Update local state
        const updatedOrders = orders.map(order => {
          if (selectedOrdersForExport.includes(order.id)) {
            const updatedOrder = { ...order, status: selectedBulkStatus };
            if (selectedBulkStatus === 'shipped') {
              updatedOrder.shipped_at = new Date().toISOString();
            }
            return updatedOrder;
          }
          return order;
        });
        
        setOrders(updatedOrders);
        calculateStats(updatedOrders);
        
        // Clear selections
        setSelectedOrdersForExport([]);
        setSelectAllOrders(false);
        setBulkStatusDialogOpen(false);
        setSelectedBulkStatus('');
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('فشل في تحديث حالة الطلبات');
      }
    } catch (error) {
      console.error('Error updating bulk status:', error);
      setError('حدث خطأ أثناء تحديث الحالات: ' + error.message);
    } finally {
      setUpdatingBulkStatus(false);
    }
  };

  // ==================== SHIPPING INTEGRATION FUNCTIONS ====================
  
  // Load shipping data on mount
  useEffect(() => {
    loadShippingData();
  }, []);

  const loadShippingData = async () => {
    setLoadingShippingData(true);
    try {
      const token = adminAuthService.getToken();
      if (!token) {
        setError('❌ لم يتم العثور على رمز التوثيق. الرجاء تسجيل الدخول مرة أخرى.');
        setLoadingShippingData(false);
        return;
      }
      
      const [citiesRes, sizesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/shipping_integration.php?action=get_cities`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/api/shipping_integration.php?action=get_package_sizes`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      const cities = await citiesRes.json();
      const sizes = await sizesRes.json();
      
      if (cities.status) {
        setShippingCities(cities.data);
      }
      if (sizes.status) {
        setPackageSizes(sizes.data);
      }
    } catch (error) {
      console.error('Error loading shipping data:', error);
      setError('فشل في تحميل بيانات الشحن');
    } finally {
      setLoadingShippingData(false);
    }
  };

  // Handle shipping city change and load regions
  const handleShippingCityChange = async (cityId) => {
    setSelectedShippingCity(cityId);
    setSelectedShippingRegion('');
    setShippingRegions([]);
    
    if (!cityId) return;
    
    try {
      const token = adminAuthService.getToken();
      if (!token) {
        setError('❌ لم يتم العثور على رمز التوثيق');
        return;
      }
      
      const response = await fetch(
        `${API_BASE_URL}/api/shipping_integration.php?action=get_regions&city_id=${cityId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      const data = await response.json();
      
      if (data.status) {
        setShippingRegions(data.data);
      } else {
        setError('فشل في تحميل المناطق');
      }
    } catch (error) {
      console.error('Error loading regions:', error);
      setError('حدث خطأ في تحميل المناطق');
    }
  };

  // Send order to shipping company
  const handleSendToShipping = async () => {
    if (!selectedOrder) return;
    
    // Validate required fields
    if (!selectedShippingCity || !selectedShippingRegion || !selectedPackageSize) {
      setError('⚠️ الرجاء اختيار المدينة والمنطقة وحجم الطرد');
      return;
    }
    
    const token = adminAuthService.getToken();
    if (!token) {
      setError('❌ لم يتم العثور على رمز التوثيق. الرجاء تسجيل الدخول مرة أخرى.');
      return;
    }
    
    setSendingToShipping(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/shipping_integration.php?action=create_order`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          order_id: selectedOrder.id,
          customer_name: selectedOrder.customer_name,
          customer_phone: selectedOrder.customer_phone,
          customer_phone2: '',
          city_id: selectedShippingCity,
          region_id: selectedShippingRegion,
          shipping_address: selectedOrder.shipping_address,
          type_name: 'منتجات متنوعة',
          items_count: selectedOrder.items?.length || 1,
          total_amount: Math.round(selectedOrder.total_amount),
          package_size: selectedPackageSize,
          notes: selectedOrder.admin_notes || selectedOrder.notes || '',
          replacement: 0
        })
      });
      
      const data = await response.json();
      
      if (data.status === true && data.data && data.data.length > 0) {
        const orderData = data.data[0];
        setSuccess(`✅ تم إرسال الطلب لشركة الشحن بنجاح!
        
رقم التتبع: ${orderData.qr_id}`);
        
        setShippingDialogOpen(false);
        loadOrders();
        
        // Open QR link in new tab
        if (orderData.qr_link) {
          window.open(orderData.qr_link, '_blank');
        }
        
        // Update selected order
        setSelectedOrder(prev => ({
          ...prev,
          shipping_qr_id: orderData.qr_id,
          shipping_qr_link: orderData.qr_link,
          shipping_status: 'sent'
        }));
        
      } else {
        setError('❌ فشل في إرسال الطلب: ' + (data.msg || 'خطأ غير معروف'));
      }
    } catch (error) {
      console.error('Error sending to shipping:', error);
      setError('⚠️ حدث خطأ في إرسال الطلب: ' + error.message);
    } finally {
      setSendingToShipping(false);
    }
  };

  // Sync shipping status from API
  const handleSyncShippingStatus = async (orderId) => {
    const token = adminAuthService.getToken();
    if (!token) {
      setError('❌ لم يتم العثور على رمز التوثيق. الرجاء تسجيل الدخول مرة أخرى.');
      return;
    }
    
    setSyncingShippingStatus(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/shipping_integration.php?action=sync_order_status&order_id=${orderId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(`✅ تم تحديث حالة الشحن: ${data.status}`);
        
        // Update orders list
        setOrders(prevOrders => 
          prevOrders.map(order => 
            order.id === orderId 
              ? { ...order, shipping_status: data.status_id, shipping_status_text: data.status }
              : order
          )
        );
        
        // Update selected order if open
        if (selectedOrder?.id === orderId) {
          setSelectedOrder(prev => ({
            ...prev,
            shipping_status: data.status_id,
            shipping_status_text: data.status
          }));
        }
      } else {
        setError('❌ فشل في مزامنة حالة الشحن');
      }
    } catch (error) {
      console.error('Error syncing shipping status:', error);
      setError('⚠️ حدث خطأ في مزامنة حالة الشحن');
    } finally {
      setSyncingShippingStatus(false);
    }
  };

  // View shipping info
  const handleViewShippingInfo = (order) => {
    if (!order.shipping_qr_id) {
      setError('⚠️ لم يتم إرسال هذا الطلب لشركة الشحن بعد');
      return;
    }
    
    // Open QR link in new tab
    if (order.shipping_qr_link) {
      window.open(order.shipping_qr_link, '_blank');
    } else {
      alert(`📦 معلومات الشحن:
━━━━━━━━━━━━━━━━━━━━
🔢 رقم التتبع: ${order.shipping_qr_id}
📊 الحالة: ${order.shipping_status_text || 'جاري الشحن'}`);
    }
  };

  // Open shipping dialog
  const handleOpenShippingDialog = (order) => {
    setSelectedOrder(order);
    setSelectedShippingCity('');
    setSelectedShippingRegion('');
    setSelectedPackageSize('');
    setShippingRegions([]);
    setShippingDialogOpen(true);
  };

  // Fetch all orders from shipping company
  const handleViewShippingCompanyOrders = async () => {
    setLoadingShippingOrders(true);
    setShippingCompanyOrdersDialogOpen(true);
    
    try {
      const token = adminAuthService.getToken();
      
      if (!token) {
        setError('❌ لم يتم العثور على رمز التوثيق. الرجاء تسجيل الدخول مرة أخرى.');
        setShippingCompanyOrders([]);
        setLoadingShippingOrders(false);
        return;
      }
      
      // Send token both in header AND query parameter for maximum compatibility
      const response = await fetch(
        `${API_BASE_URL}/api/shipping_integration.php?action=get_shipping_orders&auth_token=${encodeURIComponent(token)}`,
        {
          method: 'GET',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      const data = await response.json();
      console.log('Shipping orders response:', data);
      
      if (data.status === true) {
        if (data.data && data.data.length > 0) {
          setShippingCompanyOrders(data.data);
          setSuccess(`✅ تم جلب ${data.data.length} طلب من شركة الشحن`);
        } else {
          setShippingCompanyOrders([]);
          setSuccess('✅ تم الاتصال بنجاح - لا توجد طلبات في شركة الشحن حالياً. يمكنك إرسال طلبات جديدة باستخدام زر "إرسال إلى شركة الشحن"');
        }
      } else {
        setError('❌ فشل في جلب الطلبات: ' + (data.msg || data.error || 'خطأ غير معروف'));
        setShippingCompanyOrders([]);
      }
    } catch (error) {
      console.error('Error fetching shipping company orders:', error);
      setError('⚠️ حدث خطأ في جلب الطلبات: ' + error.message);
      setShippingCompanyOrders([]);
    } finally {
      setLoadingShippingOrders(false);
    }
  };

  // ==================== END SHIPPING INTEGRATION ====================

  return (
    <ThemeProvider theme={rtlTheme}>
      <div dir="rtl" style={{ direction: 'rtl', textAlign: 'right' }}>
        <Box sx={{ flexGrow: 1 }}>
          {/* Navigation Bar */}
          <AppBar position="static" sx={{ backgroundColor: '#10b981' }}>
            <Toolbar>
              {!isPackagingAdmin && (
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={() => navigate('/admin/dashboard')}
                  sx={{ mr: 2 }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                إدارة الطلبات - تحكم كامل
              </Typography>
              <Button
                variant="outlined"
                startIcon={<LocalShippingIcon />}
                onClick={handleViewShippingCompanyOrders}
                sx={{
                  color: 'white',
                  borderColor: 'white',
                  mr: 2,
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                View Shipping Orders
              </Button>
              <Button
                variant="contained"
                color="warning"
                startIcon={<InventoryIcon />}
                onClick={() => setMissingProductsDialogOpen(true)}
                sx={{ ml: 2 }}
              >
                المنتجات المفقودة ({missingProducts.length})
              </Button>
            </Toolbar>
          </AppBar>

          <Container maxWidth="xl" sx={{ mt: 4, mb: 4, px: { xs: 1, sm: 2 } }}>
            {/* Statistics Cards */}
            <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} sx={{ mb: { xs: 2, sm: 3 } }}>
              <Grid item xs={6} sm={4} md={2}>
                <Card 
                  sx={{ cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.05)' } }}
                  onClick={() => {
                    setStatusFilter('');
                    setTodayShippedFilter(false);
                    setDateFilter('');
                    setShippedDateFilter('');
                  }}
                >
                  <CardContent sx={{ 
                    textAlign: 'center',
                    p: { xs: 1, sm: 2 },
                    '&:last-child': { pb: { xs: 1, sm: 2 } }
                  }}>
                    <OrdersIcon sx={{ 
                      fontSize: { xs: '24px', sm: '32px', md: '40px' }, 
                      color: '#6366f1', 
                      mb: { xs: 0.5, sm: 1 }
                    }} />
                    <Typography 
                      variant={{ xs: 'h6', sm: 'h5', md: 'h4' }} 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.8rem' }
                      }}
                    >
                      {stats.total}
                    </Typography>
                    <Typography 
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        lineHeight: 1.2
                      }}
                    >
                      إجمالي الطلبات
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Card 
                  sx={{ cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.05)' } }}
                  onClick={() => {
                    setStatusFilter('confirmed');
                    setTodayShippedFilter(false);
                    setDateFilter('');
                    setShippedDateFilter('');
                  }}
                >
                  <CardContent sx={{ 
                    textAlign: 'center',
                    p: { xs: 1, sm: 2 },
                    '&:last-child': { pb: { xs: 1, sm: 2 } }
                  }}>
                    <CheckIcon sx={{ 
                      fontSize: { xs: '24px', sm: '32px', md: '40px' }, 
                      color: '#3b82f6', 
                      mb: { xs: 0.5, sm: 1 }
                    }} />
                    <Typography 
                      variant={{ xs: 'h6', sm: 'h5', md: 'h4' }} 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.8rem' }
                      }}
                    >
                      {stats.confirmed}
                    </Typography>
                    <Typography 
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        lineHeight: 1.2
                      }}
                    >
                      مؤكدة
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Card 
                  sx={{ 
                    backgroundColor: '#f0fdf4', 
                    border: '2px solid #10b981',
                    cursor: 'pointer', 
                    transition: 'transform 0.2s', 
                    '&:hover': { transform: 'scale(1.05)' } 
                  }}
                  onClick={() => {
                    setStatusFilter('shipped');
                    setTodayShippedFilter(false);
                    setDateFilter('');
                    setShippedDateFilter('');
                  }}
                >
                  <CardContent sx={{ 
                    textAlign: 'center',
                    p: { xs: 1, sm: 2 },
                    '&:last-child': { pb: { xs: 1, sm: 2 } }
                  }}>
                    <ShippingIcon sx={{ 
                      fontSize: { xs: '24px', sm: '32px', md: '40px' }, 
                      color: '#10b981', 
                      mb: { xs: 0.5, sm: 1 }
                    }} />
                    <Typography 
                      variant={{ xs: 'h6', sm: 'h5', md: 'h4' }} 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.8rem' },
                        color: '#10b981'
                      }}
                    >
                      {stats.shipped}
                    </Typography>
                    <Typography 
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        lineHeight: 1.2,
                        color: '#065f46',
                        fontWeight: 600
                      }}
                    >
                      تم الشحن
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Card 
                  sx={{ cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.05)' } }}
                  onClick={() => {
                    setStatusFilter('delivered');
                    setTodayShippedFilter(false);
                    setDateFilter('');
                    setShippedDateFilter('');
                  }}
                >
                  <CardContent sx={{ 
                    textAlign: 'center',
                    p: { xs: 1, sm: 2 },
                    '&:last-child': { pb: { xs: 1, sm: 2 } }
                  }}>
                    <CheckIcon sx={{ 
                      fontSize: { xs: '24px', sm: '32px', md: '40px' }, 
                      color: '#10b981', 
                      mb: { xs: 0.5, sm: 1 }
                    }} />
                    <Typography 
                      variant={{ xs: 'h6', sm: 'h5', md: 'h4' }} 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.8rem' }
                      }}
                    >
                      {stats.delivered}
                    </Typography>
                    <Typography 
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        lineHeight: 1.2
                      }}
                    >
                      تم التوصيل
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Card 
                  sx={{ cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.05)' } }}
                  onClick={() => {
                    setStatusFilter('pending');
                    setTodayShippedFilter(false);
                    setDateFilter('');
                    setShippedDateFilter('');
                  }}
                >
                  <CardContent sx={{ 
                    textAlign: 'center',
                    p: { xs: 1, sm: 2 },
                    '&:last-child': { pb: { xs: 1, sm: 2 } }
                  }}>
                    <PendingIcon sx={{ 
                      fontSize: { xs: '24px', sm: '32px', md: '40px' }, 
                      color: '#f59e0b', 
                      mb: { xs: 0.5, sm: 1 }
                    }} />
                    <Typography 
                      variant={{ xs: 'h6', sm: 'h5', md: 'h4' }} 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.8rem' }
                      }}
                    >
                      {stats.pending}
                    </Typography>
                    <Typography 
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        lineHeight: 1.2
                      }}
                    >
                      في الانتظار
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Card 
                  sx={{ 
                    backgroundColor: '#fef3c7', 
                    border: '2px solid #f59e0b',
                    cursor: 'pointer', 
                    transition: 'transform 0.2s', 
                    '&:hover': { transform: 'scale(1.05)' } 
                  }}
                  onClick={() => {
                    setStatusFilter('');
                    setTodayShippedFilter(true);
                    setDateFilter('');
                    setShippedDateFilter('');
                  }}
                >
                  <CardContent sx={{ 
                    textAlign: 'center',
                    p: { xs: 1, sm: 2 },
                    '&:last-child': { pb: { xs: 1, sm: 2 } }
                  }}>
                    <ShippingIcon sx={{ 
                      fontSize: { xs: '24px', sm: '32px', md: '40px' }, 
                      color: '#f59e0b', 
                      mb: { xs: 0.5, sm: 1 }
                    }} />
                    <Typography 
                      variant={{ xs: 'h6', sm: 'h5', md: 'h4' }} 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.8rem' },
                        color: '#f59e0b'
                      }}
                    >
                      {stats.todayShipped}
                    </Typography>
                    <Typography 
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        lineHeight: 1.2,
                        color: '#92400e',
                        fontWeight: 600
                      }}
                    >
                      شحن اليوم
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Card 
                  sx={{ cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.05)' } }}
                  onClick={() => {
                    setStatusFilter('cancelled');
                    setTodayShippedFilter(false);
                    setDateFilter('');
                    setShippedDateFilter('');
                  }}
                >
                  <CardContent sx={{ 
                    textAlign: 'center',
                    p: { xs: 1, sm: 2 },
                    '&:last-child': { pb: { xs: 1, sm: 2 } }
                  }}>
                    <CancelIcon sx={{ 
                      fontSize: { xs: '24px', sm: '32px', md: '40px' }, 
                      color: '#ef4444', 
                      mb: { xs: 0.5, sm: 1 }
                    }} />
                    <Typography 
                      variant={{ xs: 'h6', sm: 'h5', md: 'h4' }} 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.8rem' }
                      }}
                    >
                      {stats.cancelled}
                    </Typography>
                    <Typography 
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        lineHeight: 1.2
                      }}
                    >
                      ملغية
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Error/Success Messages */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            
            {success && (
              <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}

            {/* Search and Filters */}
            <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
              <Grid container spacing={{ xs: 2, sm: 3 }}>
                <Grid item xs={12} sm={12} md={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="البحث في الطلبات (رقم الطلب، اسم العميل، رقم الهاتف)..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    disabled={bulkOrderIds.trim().length > 0 || bulkPhoneNumbers.trim().length > 0}
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setSearchQuery('')}
                            edge="end"
                          >
                            <CancelIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="بحث بأرقام طلبات متعددة (مفصولة بفاصلة أو مسافة: 123, 456, 789)"
                    value={bulkOrderIds}
                    onChange={(e) => setBulkOrderIds(e.target.value)}
                    multiline
                    rows={2}
                    size={isMobile ? "small" : "medium"}
                    disabled={bulkPhoneNumbers.trim().length > 0}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#6366f1' }} />
                        </InputAdornment>
                      ),
                      endAdornment: bulkOrderIds && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setBulkOrderIds('')}
                            edge="end"
                          >
                            <CancelIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      backgroundColor: bulkOrderIds.trim() ? '#f0f9ff' : 'white',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: bulkOrderIds.trim() ? '#6366f1' : undefined,
                          borderWidth: bulkOrderIds.trim() ? '2px' : '1px'
                        }
                      }
                    }}
                  />
                  {bulkOrderIds.trim() && (
                    <Typography variant="caption" sx={{ color: '#6366f1', fontWeight: 600, mt: 0.5, display: 'block' }}>
                      {(() => {
                        const count = bulkOrderIds
                          .split(/[,\s]+/)
                          .map(id => id.trim())
                          .filter(id => id.length > 0).length;
                        return `🔍 البحث عن ${count} طلب - عرض ${filteredOrders.length} نتيجة`;
                      })()}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="بحث بأرقام هواتف متعددة (مفصولة بفاصلة أو مسافة: 07812345678, 07723456789)"
                    value={bulkPhoneNumbers}
                    onChange={(e) => setBulkPhoneNumbers(e.target.value)}
                    multiline
                    rows={2}
                    size={isMobile ? "small" : "medium"}
                    disabled={bulkOrderIds.trim().length > 0}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon sx={{ color: '#10b981' }} />
                        </InputAdornment>
                      ),
                      endAdornment: bulkPhoneNumbers && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setBulkPhoneNumbers('')}
                            edge="end"
                          >
                            <CancelIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    sx={{
                      backgroundColor: bulkPhoneNumbers.trim() ? '#f0fdf4' : 'white',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: bulkPhoneNumbers.trim() ? '#10b981' : undefined,
                          borderWidth: bulkPhoneNumbers.trim() ? '2px' : '1px'
                        }
                      }
                    }}
                  />
                  {bulkPhoneNumbers.trim() && (
                    <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 600, mt: 0.5, display: 'block' }}>
                      {(() => {
                        const count = bulkPhoneNumbers
                          .split(/[,\s]+/)
                          .map(phone => phone.trim())
                          .filter(phone => phone.length > 0).length;
                        return `📞 البحث عن ${count} رقم هاتف - عرض ${filteredOrders.length} نتيجة`;
                      })()}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>تصفية حسب الحالة</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      label="تصفية حسب الحالة"
                    >
                      <MenuItem value="">كل الحالات</MenuItem>
                      {statusOptions.map((status) => (
                        <MenuItem key={status.value} value={status.value}>
                          {status.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>تصفية حسب التاريخ</InputLabel>
                    <Select
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value)}
                      label="تصفية حسب التاريخ"
                    >
                      <MenuItem value="">كل الفترات</MenuItem>
                      <MenuItem value="today">اليوم</MenuItem>
                      <MenuItem value="yesterday">أمس</MenuItem>
                      <MenuItem value="thisWeek">هذا الأسبوع</MenuItem>
                      <MenuItem value="lastWeek">الأسبوع الماضي</MenuItem>
                      <MenuItem value="last7days">آخر 7 أيام</MenuItem>
                      <MenuItem value="thisMonth">هذا الشهر</MenuItem>
                      <MenuItem value="lastMonth">الشهر الماضي</MenuItem>
                      <MenuItem value="last30days">آخر 30 يوم</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    type="date"
                    fullWidth
                    label="تاريخ الشحن"
                    value={shippedDateFilter}
                    onChange={(e) => setShippedDateFilter(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    helperText="تصفية الطلبات المشحونة بتاريخ محدد"
                    sx={{
                      '& input': {
                        textAlign: 'left'
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant={websiteOrdersOnly ? "contained" : "outlined"}
                    fullWidth
                    onClick={() => setWebsiteOrdersOnly(!websiteOrdersOnly)}
                    sx={{ 
                      height: isMobile ? '40px' : '56px',
                      backgroundColor: websiteOrdersOnly ? '#8b5cf6' : 'transparent',
                      color: websiteOrdersOnly ? 'white' : 'inherit',
                      borderColor: websiteOrdersOnly ? '#8b5cf6' : undefined,
                      '&:hover': {
                        backgroundColor: websiteOrdersOnly ? '#7c3aed' : 'rgba(139, 92, 246, 0.1)',
                        borderColor: '#8b5cf6'
                      }
                    }}
                  >
                    {websiteOrdersOnly ? '✓ طلبات الموقع فقط' : 'طلبات الموقع فقط'}
                  </Button>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => {
                      setSearchQuery('');
                      setBulkOrderIds('');
                      setBulkPhoneNumbers('');
                      setStatusFilter('');
                      setTodayShippedFilter(false);
                      setDateFilter('');
                      setShippedDateFilter('');
                      setWebsiteOrdersOnly(false);
                    }}
                    sx={{ height: isMobile ? '40px' : '56px' }}
                  >
                    إزالة التصفية
                  </Button>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<AddIcon />}
                    onClick={() => setCreateOrderDialogOpen(true)}
                    sx={{ 
                      height: isMobile ? '40px' : '56px',
                      backgroundColor: '#8b5cf6',
                      '&:hover': {
                        backgroundColor: '#7c3aed'
                      }
                    }}
                  >
                    إنشاء طلب جديد
                  </Button>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={generateDeliveryCSV}
                    disabled={selectedOrdersForExport.length === 0}
                    sx={{ 
                      height: isMobile ? '40px' : '56px',
                      backgroundColor: '#10b981',
                      '&:hover': {
                        backgroundColor: '#059669'
                      }
                    }}
                  >
                    تصدير توصيل ({selectedOrdersForExport.length})
                  </Button>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={generateStockExport}
                    disabled={selectedOrdersForExport.length === 0}
                    sx={{ 
                      height: isMobile ? '40px' : '56px',
                      backgroundColor: '#6366f1',
                      '&:hover': {
                        backgroundColor: '#4f46e5'
                      }
                    }}
                  >
                    تصدير مخزون ({selectedOrdersForExport.length})
                  </Button>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<EditIcon />}
                    onClick={() => setBulkStatusDialogOpen(true)}
                    disabled={selectedOrdersForExport.length === 0}
                    sx={{ 
                      height: isMobile ? '40px' : '56px',
                      backgroundColor: '#f59e0b',
                      '&:hover': {
                        backgroundColor: '#d97706'
                      }
                    }}
                  >
                    تغيير الحالة ({selectedOrdersForExport.length})
                  </Button>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<ReceiptIcon />}
                    onClick={handleCheckPrice}
                    disabled={selectedOrdersForExport.length === 0}
                    sx={{ 
                      height: isMobile ? '40px' : '56px',
                      backgroundColor: '#0f766e',
                      '&:hover': {
                        backgroundColor: '#115e59'
                      }
                    }}
                  >
                    Check price ({selectedOrdersForExport.length})
                  </Button>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<WhatsAppIcon />}
                    onClick={() => setBulkWhatsAppDialogOpen(true)}
                    disabled={selectedOrdersForExport.length === 0}
                    sx={{ 
                      height: isMobile ? '40px' : '56px',
                      backgroundColor: '#25D366',
                      '&:hover': {
                        backgroundColor: '#1fae57'
                      }
                    }}
                  >
                    واتساب جماعي ({selectedOrdersForExport.length})
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {/* Orders Table */}
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
              {isMobile ? (
                /* Mobile Card View */
                <Box sx={{ p: 2 }}>
                  {/* Select All Checkbox for Mobile */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 2,
                    pb: 2,
                    borderBottom: '2px solid #e5e7eb'
                  }}>
                    <Checkbox
                      color="primary"
                      checked={selectAllOrders}
                      onChange={handleSelectAllOrdersForExport}
                      sx={{ 
                        color: '#6b7280',
                        '&.Mui-checked': { color: '#10b981' }
                      }}
                    />
                    <Typography sx={{ fontWeight: 600, color: '#374151' }}>
                      تحديد الكل ({selectedOrdersForExport.length} محدد)
                    </Typography>
                  </Box>
                  
                  {loading ? (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      gap: 2,
                      py: 4
                    }}>
                      <Box sx={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        border: '4px solid #e0e7ff',
                        borderTop: '4px solid #4169E1',
                        animation: 'spin 1s linear infinite',
                        '@keyframes spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' }
                        }
                      }} />
                      <Typography>جاري تحميل الطلبات...</Typography>
                    </Box>
                  ) : filteredOrders.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <OrdersIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                      <Typography>لا توجد طلبات</Typography>
                    </Box>
                  ) : (
                    <Stack spacing={2}>
                      {(rowsPerPage > 0
                        ? filteredOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        : filteredOrders
                      ).map((order) => (
                          <Card 
                            key={order.id} 
                            sx={{ 
                              border: order.status === 'shipped' ? '2px solid #10b981' : '1px solid #e0e0e0',
                              backgroundColor: order.status === 'shipped' ? '#f0fdf4' : 'white',
                              borderRadius: 2
                            }}
                          >
                            <CardContent sx={{ p: 2 }}>
                              {/* Order Header */}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                  <Checkbox
                                    color="primary"
                                    checked={selectedOrdersForExport.includes(order.id)}
                                    onChange={() => handleSelectOrderForExport(order.id)}
                                    sx={{ 
                                      color: '#6b7280',
                                      '&.Mui-checked': { color: '#10b981' },
                                      p: 0,
                                      mt: 0.5
                                    }}
                                  />
                                  <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                      طلب #{order.id}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {new Date(order.created_at).toLocaleDateString('ar-EG')}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Chip
                                  {...getStatusInfo(order.status)}
                                  label={getStatusInfo(order.status).label}
                                  size="small"
                                  sx={{
                                    backgroundColor: order.status === 'shipped' ? '#10b981' : undefined,
                                    color: order.status === 'shipped' ? 'white' : undefined,
                                    fontWeight: order.status === 'shipped' ? 'bold' : undefined
                                  }}
                                />
                              </Box>
                              
                              {/* Customer Info */}
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                  {order.customer_name || 'غير محدد'}
                                </Typography>
                                {order.customer_phone && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                      {order.customer_phone}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        navigator.clipboard.writeText(order.customer_phone);
                                        setSuccess('تم نسخ رقم الهاتف');
                                        setTimeout(() => setSuccess(''), 2000);
                                      }}
                                      sx={{ 
                                        color: '#6366f1', 
                                        backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                        '&:hover': { 
                                          backgroundColor: 'rgba(99, 102, 241, 0.2)' 
                                        },
                                        p: 0.5
                                      }}
                                    >
                                      <ContentCopyIcon sx={{ fontSize: '14px' }} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleWhatsAppClick(order.customer_phone)}
                                      sx={{ 
                                        color: '#25D366', 
                                        backgroundColor: 'rgba(37, 211, 102, 0.1)',
                                        '&:hover': { 
                                          backgroundColor: 'rgba(37, 211, 102, 0.2)' 
                                        },
                                        p: 0.5
                                      }}
                                    >
                                      <PhoneIcon sx={{ fontSize: '16px' }} />
                                    </IconButton>
                                  </Box>
                                )}
                              </Box>
                              
                              {/* Points Info */}
                              <Box sx={{ 
                                mb: 2,
                                p: 1.5,
                                backgroundColor: '#fef3c7',
                                borderRadius: '8px',
                                border: '1px solid #fbbf24'
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="caption" sx={{ color: '#78350f', fontWeight: 600 }}>
                                    نقاط العميل:
                                  </Typography>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontSize: '0.875rem',
                                      fontWeight: 700,
                                      color: '#f59e0b'
                                    }}
                                  >
                                    {order.user_points || 0} نقطة
                                  </Typography>
                                </Box>
                              </Box>
                              
                              {/* Status Change */}
                              <Box>
                                <Typography variant="caption" sx={{ color: '#6b7280', mb: 0.5, display: 'block' }}>
                                  تغيير الحالة:
                                </Typography>
                                <FormControl size="small" fullWidth>
                                  <Select
                                    value={order.status}
                                    onChange={(e) => handleStatusChange(order.id, e.target.value)}
                                    sx={{ 
                                      fontSize: '0.875rem',
                                      borderRadius: '8px',
                                      backgroundColor: '#f8fafc',
                                      border: '1px solid #e2e8f0',
                                      '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none'
                                      }
                                    }}
                                  >
                                    {statusOptions.map((status) => (
                                      <MenuItem key={status.value} value={status.value}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          {React.cloneElement(status.icon, { 
                                            sx: { fontSize: '16px', color: getStatusColor(status.color) } 
                                          })}
                                          <Typography sx={{ fontSize: '0.875rem' }}>
                                            {status.label}
                                          </Typography>
                                        </Box>
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Box>
                              
                              {/* View Details Button */}
                              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                                <Button
                                  variant="outlined"
                                  fullWidth
                                  startIcon={<ViewIcon />}
                                  onClick={() => handleViewOrderDetails(order)}
                                  sx={{
                                    borderColor: '#4169E1',
                                    color: '#4169E1',
                                    '&:hover': {
                                      borderColor: '#2952cc',
                                      backgroundColor: 'rgba(65, 105, 225, 0.05)'
                                    }
                                  }}
                                >
                                  عرض التفاصيل
                                </Button>
                                <Button
                                  variant="contained"
                                  startIcon={<WhatsAppIcon />}
                                  onClick={() => {
                                    setSelectedOrderForWhatsApp(order);
                                    setWhatsappDialogOpen(true);
                                  }}
                                  sx={{
                                    backgroundColor: '#25D366',
                                    color: 'white',
                                    minWidth: '120px',
                                    '&:hover': {
                                      backgroundColor: '#1fb855'
                                    }
                                  }}
                                >
                                  واتساب
                                </Button>
                              </Box>
                            </CardContent>
                          </Card>
                        ))}
                    </Stack>
                  )}
                </Box>
              ) : (
                /* Desktop Table View */
                <TableContainer 
                  sx={{
                    borderRadius: '12px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                    border: '1px solid #e0e7ff',
                    overflow: 'hidden',
                    backgroundColor: 'white'
                  }}
                >
                  <Table sx={{ minWidth: 650 }}>
                    <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ fontWeight: 700 }}>
                          <Checkbox
                            color="primary"
                            checked={selectAllOrders}
                            onChange={handleSelectAllOrdersForExport}
                            sx={{ 
                              color: '#6b7280',
                              '&.Mui-checked': { color: '#10b981' }
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>
                          رقم الطلب
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>
                          العميل
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>
                          رقم الهاتف
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>
                          النقاط
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>
                          الحالة
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>
                          حالة الشحن
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>
                          تاريخ الطلب
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>
                          الإجراءات
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell 
                            colSpan={9} 
                            align="center"
                            sx={{
                              padding: '60px 20px',
                              backgroundColor: '#fafbfc'
                            }}
                          >
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              alignItems: 'center',
                              gap: 2
                            }}>
                              <Box sx={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                border: '4px solid #e0e7ff',
                                borderTop: '4px solid #4169E1',
                                animation: 'spin 1s linear infinite',
                                '@keyframes spin': {
                                  '0%': { transform: 'rotate(0deg)' },
                                  '100%': { transform: 'rotate(360deg)' }
                                }
                              }} />
                              <Typography 
                                variant="body1" 
                                sx={{ 
                                  color: '#6b7280',
                                  fontSize: '1rem',
                                  fontWeight: 500
                                }}
                              >
                                جاري تحميل الطلبات...
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : filteredOrders.length === 0 ? (
                        <TableRow>
                          <TableCell 
                            colSpan={9} 
                            align="center"
                            sx={{
                              padding: '60px 20px',
                              backgroundColor: '#fafbfc'
                            }}
                          >
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              alignItems: 'center',
                              gap: 2
                            }}>
                              <Box sx={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '50%',
                                backgroundColor: '#f3f4f6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: 1
                              }}>
                                <OrdersIcon sx={{ fontSize: '30px', color: '#9ca3af' }} />
                              </Box>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  color: '#374151',
                                  fontSize: '1.1rem',
                                  fontWeight: 600,
                                  marginBottom: 0.5
                                }}
                              >
                                لا توجد طلبات
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: '#6b7280',
                                  fontSize: '0.9rem',
                                  textAlign: 'center'
                                }}
                              >
                                لم يتم العثور على أي طلبات تطابق معايير البحث الحالية
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        (rowsPerPage > 0
                          ? filteredOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          : filteredOrders
                        ).map((order) => (
                            <TableRow 
                              key={order.id}
                              sx={{
                                backgroundColor: order.status === 'shipped' ? '#f0fdf4' : 'white',
                                borderLeft: order.status === 'shipped' ? '4px solid #10b981' : 'none',
                                '&:hover': {
                                  backgroundColor: order.status === 'shipped' ? '#dcfce7' : '#f8fafc',
                                  transition: 'all 0.2s ease'
                                }
                              }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  color="primary"
                                  checked={selectedOrdersForExport.includes(order.id)}
                                  onChange={() => handleSelectOrderForExport(order.id)}
                                  sx={{ 
                                    color: '#6b7280',
                                    '&.Mui-checked': { color: '#10b981' }
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                                <Typography 
                                  variant="subtitle1" 
                                  sx={{ 
                                    fontWeight: 600,
                                    color: order.status === 'shipped' ? '#065f46' : '#4169E1',
                                    fontSize: '0.95rem'
                                  }}
                                >
                                  #{order.id}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                                <Typography 
                                  variant="subtitle2" 
                                  sx={{ 
                                    fontWeight: 600, 
                                    color: '#111827',
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  {order.customer_name || 'غير محدد'}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                                {order.customer_phone ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                      {order.customer_phone}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        navigator.clipboard.writeText(order.customer_phone);
                                        setSuccess('تم نسخ رقم الهاتف');
                                        setTimeout(() => setSuccess(''), 2000);
                                      }}
                                      sx={{ 
                                        color: '#6366f1',
                                        backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                        '&:hover': {
                                          backgroundColor: 'rgba(99, 102, 241, 0.2)',
                                          transform: 'scale(1.05)'
                                        },
                                        transition: 'all 0.2s ease',
                                        borderRadius: '50%',
                                        padding: '4px'
                                      }}
                                    >
                                      <ContentCopyIcon sx={{ fontSize: '14px' }} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleWhatsAppClick(order.customer_phone)}
                                      sx={{ 
                                        color: '#25D366',
                                        backgroundColor: 'rgba(37, 211, 102, 0.1)',
                                        '&:hover': {
                                          backgroundColor: 'rgba(37, 211, 102, 0.2)',
                                          transform: 'scale(1.05)'
                                        },
                                        transition: 'all 0.2s ease',
                                        borderRadius: '50%',
                                        padding: '4px'
                                      }}
                                    >
                                      <PhoneIcon sx={{ fontSize: '16px' }} />
                                    </IconButton>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    غير محدد
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontSize: '0.875rem',
                                      fontWeight: 600,
                                      color: '#f59e0b'
                                    }}
                                  >
                                    {order.user_points || 0}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    نقطة
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                                <FormControl size="small" sx={{ minWidth: 140 }}>
                                  <Select
                                    value={order.status}
                                    onChange={(e) => handleStatusChange(order.id, e.target.value)}
                                    displayEmpty
                                    sx={{ 
                                      fontSize: '0.875rem',
                                      borderRadius: '8px',
                                      backgroundColor: '#f8fafc',
                                      border: '1px solid #e2e8f0',
                                      '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none'
                                      },
                                      '&:hover': {
                                        backgroundColor: '#f1f5f9',
                                        border: '1px solid #cbd5e1'
                                      },
                                      '&.Mui-focused': {
                                        backgroundColor: 'white',
                                        boxShadow: '0 0 0 2px rgba(16, 185, 129, 0.1)',
                                        border: '1px solid #10b981'
                                      },
                                      '& .MuiSelect-select': {
                                        paddingY: '8px',
                                        paddingX: '12px',
                                        display: 'flex',
                                        alignItems: 'center'
                                      }
                                    }}
                                  >
                                    {statusOptions.map((status) => (
                                      <MenuItem 
                                        key={status.value} 
                                        value={status.value}
                                        sx={{
                                          '&:hover': {
                                            backgroundColor: '#f8fafc'
                                          }
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          {React.cloneElement(status.icon, { 
                                            sx: { fontSize: '16px', color: getStatusColor(status.color) } 
                                          })}
                                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                                            {status.label}
                                          </Typography>
                                        </Box>
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </TableCell>
                              
                              {/* Shipping Status Column */}
                              <TableCell sx={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                                {order.shipping_status === 'sent' || order.shipping_qr_id ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                    <Chip 
                                      label={order.shipping_status_text || 'تم الإرسال'} 
                                      color="success"
                                      size="small"
                                      icon={<LocalShippingIcon />}
                                      sx={{ 
                                        fontSize: '0.75rem',
                                        height: '24px',
                                        backgroundColor: '#10b981',
                                        color: 'white'
                                      }}
                                    />
                                    {order.shipping_qr_link && (
                                      <IconButton
                                        size="small"
                                        onClick={() => window.open(order.shipping_qr_link, '_blank')}
                                        sx={{ 
                                          color: '#10b981',
                                          padding: '2px'
                                        }}
                                        title="طباعة الملصق"
                                      >
                                        <ReceiptIcon sx={{ fontSize: '16px' }} />
                                      </IconButton>
                                    )}
                                    <IconButton
                                      size="small"
                                      onClick={() => handleSyncShippingStatus(order.id)}
                                      disabled={syncingShippingStatus}
                                      sx={{ 
                                        color: '#6366f1',
                                        padding: '2px'
                                      }}
                                      title="تحديث الحالة"
                                    >
                                      <RefreshIcon sx={{ fontSize: '16px' }} />
                                    </IconButton>
                                  </Box>
                                ) : (
                                  <Chip 
                                    label="لم يتم الإرسال" 
                                    variant="outlined"
                                    size="small"
                                    sx={{ fontSize: '0.75rem', height: '24px' }}
                                  />
                                )}
                              </TableCell>
                              
                              <TableCell sx={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: '#6b7280',
                                    fontSize: '0.875rem',
                                    fontWeight: 500
                                  }}
                                >
                                  {new Date(order.created_at).toLocaleDateString('ar-EG')}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewOrderDetails(order)}
                                    sx={{ 
                                      color: '#4169E1',
                                      backgroundColor: 'rgba(65, 105, 225, 0.1)',
                                      '&:hover': {
                                        backgroundColor: 'rgba(65, 105, 225, 0.2)',
                                        transform: 'scale(1.05)'
                                      },
                                      transition: 'all 0.2s ease',
                                      borderRadius: '8px',
                                      padding: '6px'
                                    }}
                                    title="عرض التفاصيل"
                                  >
                                    <ViewIcon sx={{ fontSize: '18px' }} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setSelectedOrderForWhatsApp(order);
                                      setWhatsappDialogOpen(true);
                                    }}
                                    sx={{ 
                                      color: 'white',
                                      backgroundColor: '#25D366',
                                      '&:hover': {
                                        backgroundColor: '#1fb855',
                                        transform: 'scale(1.05)'
                                      },
                                      transition: 'all 0.2s ease',
                                      borderRadius: '8px',
                                      padding: '6px'
                                    }}
                                    title="إرسال رسالة واتساب"
                                  >
                                    <WhatsAppIcon sx={{ fontSize: '18px' }} />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              
              <TablePagination
                component="div"
                count={filteredOrders.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100, { label: 'الكل', value: -1 }]}
                labelRowsPerPage={isMobile ? "الصفوف:" : "عدد الصفوف في الصفحة:"}
                labelDisplayedRows={({ from, to, count }) => 
                  isMobile ? `${from}-${to}/${count}` : `${from}-${to} من أصل ${count !== -1 ? count : `أكثر من ${to}`}`
                }
                sx={{
                  backgroundColor: '#f8fafc',
                  borderTop: '1px solid #e2e8f0',
                  borderRadius: '0 0 12px 12px',
                  '& .MuiTablePagination-toolbar': {
                    px: { xs: 2, sm: 3 },
                    minHeight: { xs: 52, sm: 56 },
                    backgroundColor: '#f8fafc'
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    fontWeight: 500,
                    color: '#374151'
                  },
                  '& .MuiTablePagination-select': {
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    padding: '4px 8px',
                    fontSize: '0.875rem'
                  },
                  '& .MuiTablePagination-actions': {
                    '& .MuiIconButton-root': {
                      backgroundColor: 'white',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      margin: '0 2px',
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                        borderColor: '#9ca3af'
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#f9fafb',
                        borderColor: '#e5e7eb',
                        color: '#d1d5db'
                      }
                    }
                  }
                }}
              />
            </Paper>
          </Container>
        </Box>

        {/* Order Details Dialog */}
        <Dialog 
          open={detailsDialogOpen} 
          onClose={() => {
            setDetailsDialogOpen(false);
            setEditingItems(false);
          }} 
          maxWidth="lg" 
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: { 
              height: isMobile ? '100vh' : 'auto',
              maxHeight: isMobile ? '100vh' : '90vh',
              display: 'flex',
              flexDirection: 'column'
            }
          }}
        >
          <DialogTitle sx={{ pb: 1, px: { xs: 2, sm: 3 } }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              flexWrap: isMobile ? 'wrap' : 'nowrap',
              gap: 1
            }}>
              <Typography 
                variant={{ xs: 'h6', sm: 'h5' }} 
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: { xs: '1.25rem', sm: '1.5rem' }
                }}
              >
                تفاصيل الطلب #{selectedOrder?.id}
              </Typography>
              {selectedOrder && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    select
                    size="small"
                    value={selectedOrder.status}
                    onChange={(e) => handleStatusChange(selectedOrder.id, e.target.value)}
                    sx={{ minWidth: 150 }}
                    SelectProps={{
                      native: true
                    }}
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </TextField>
                  <Chip
                    icon={getStatusInfo(selectedOrder.status).icon}
                    label={getStatusInfo(selectedOrder.status).label}
                    color={getStatusInfo(selectedOrder.status).color}
                    size={isMobile ? "medium" : "large"}
                    sx={{ 
                      fontSize: { xs: '0.75rem', sm: '0.9rem' }, 
                      px: { xs: 1, sm: 2 }
                    }}
                  />
                </Box>
              )}
            </Box>
          </DialogTitle>
          
          <DialogContent sx={{ 
            p: { xs: 2, sm: 3, md: 4 },
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden'
          }}>
            {selectedOrder && (
              <Box>
                {/* Essential Order Information */}
                <Paper sx={{ p: { xs: 2, sm: 3 }, backgroundColor: '#f8f9fa' }}>
                  <Typography 
                    variant={{ xs: 'h6', sm: 'h6' }} 
                    sx={{ 
                      mb: 3, 
                      color: '#10b981', 
                      fontWeight: 'bold',
                      fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}
                  >
                    📋 معلومات الطلب
                  </Typography>
                  
                  <Box sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    backgroundColor: 'white', 
                    borderRadius: 2, 
                    border: '2px solid #10b981' 
                  }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={6}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            رقم الطلب
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#10b981', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            #{selectedOrder.id}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={6} sm={6}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            اسم العميل
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            {selectedOrder.customer_name || 'غير محدد'}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={6} sm={6}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            رقم الهاتف
                          </Typography>
                          {isEditingPhone ? (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                              <TextField
                                value={phoneValue}
                                onChange={(e) => setPhoneValue(e.target.value)}
                                size="small"
                                placeholder="07XXXXXXXXX"
                                sx={{ flexGrow: 1, minWidth: '150px' }}
                                autoFocus
                              />
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton
                                  size="small"
                                  onClick={handleSavePhone}
                                  disabled={savingPhone}
                                  sx={{ 
                                    color: 'white',
                                    backgroundColor: '#10b981',
                                    '&:hover': { 
                                      backgroundColor: '#059669'
                                    },
                                    p: 0.5
                                  }}
                                  title="حفظ"
                                >
                                  <CheckIcon sx={{ fontSize: '18px' }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setIsEditingPhone(false);
                                    setPhoneValue(selectedOrder.customer_phone || '');
                                  }}
                                  disabled={savingPhone}
                                  sx={{ 
                                    color: 'white',
                                    backgroundColor: '#ef4444',
                                    '&:hover': { 
                                      backgroundColor: '#dc2626'
                                    },
                                    p: 0.5
                                  }}
                                  title="إلغاء"
                                >
                                  <CloseIcon sx={{ fontSize: '18px' }} />
                                </IconButton>
                              </Box>
                            </Box>
                          ) : selectedOrder.customer_phone ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                              <Typography variant="body1" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1.1rem' } }}>
                                {selectedOrder.customer_phone}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setPhoneValue(selectedOrder.customer_phone);
                                    setIsEditingPhone(true);
                                  }}
                                  sx={{ 
                                    color: '#3b82f6', 
                                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                    '&:hover': { 
                                      backgroundColor: 'rgba(59, 130, 246, 0.2)',
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s ease',
                                    p: { xs: 0.5, sm: 1 }
                                  }}
                                  title="تعديل رقم الهاتف"
                                >
                                  <EditIcon sx={{ fontSize: { xs: '14px', sm: '18px' } }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    navigator.clipboard.writeText(selectedOrder.customer_phone);
                                    setSuccess('تم نسخ رقم الهاتف');
                                    setTimeout(() => setSuccess(''), 2000);
                                  }}
                                  sx={{ 
                                    color: '#6366f1', 
                                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                    '&:hover': { 
                                      backgroundColor: 'rgba(99, 102, 241, 0.2)',
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s ease',
                                    p: { xs: 0.5, sm: 1 }
                                  }}
                                  title="نسخ رقم الهاتف"
                                >
                                  <ContentCopyIcon sx={{ fontSize: { xs: '14px', sm: '18px' } }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleWhatsAppClick(selectedOrder.customer_phone)}
                                  sx={{ 
                                    color: '#25D366', 
                                    backgroundColor: 'rgba(37, 211, 102, 0.1)',
                                    '&:hover': { 
                                      backgroundColor: 'rgba(37, 211, 102, 0.2)',
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s ease',
                                    p: { xs: 0.5, sm: 1 }
                                  }}
                                  title="إرسال رسالة واتساب"
                                >
                                  <PhoneIcon sx={{ fontSize: { xs: '14px', sm: '18px' } }} />
                                </IconButton>
                              </Box>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <Typography variant="body1" color="text.secondary">
                                غير محدد
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setPhoneValue('');
                                  setIsEditingPhone(true);
                                }}
                                sx={{ 
                                  color: '#3b82f6', 
                                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                  '&:hover': { 
                                    backgroundColor: 'rgba(59, 130, 246, 0.2)'
                                  },
                                  p: 0.5
                                }}
                                title="إضافة رقم الهاتف"
                              >
                                <EditIcon sx={{ fontSize: '16px' }} />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                      
                      <Grid item xs={6} sm={6}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            المدينة
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1.1rem' } }}>
                            {selectedOrder.city || 'غير محدد'}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            العنوان الكامل
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1.1rem' }, lineHeight: 1.6 }}>
                            {selectedOrder.shipping_address || 'غير محدد'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>

                {/* Admin Notes Section */}
                <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 3, backgroundColor: '#f8f9fa' }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mb: 1.5,
                    flexWrap: 'wrap',
                    gap: 1
                  }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 600, 
                        color: '#4169E1',
                        fontSize: { xs: '0.95rem', sm: '1.1rem' },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      <span>📝</span> ملاحظات الإدارة
                    </Typography>
                    {!isEditingAdminNotes ? (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setIsEditingAdminNotes(true)}
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          py: { xs: 0.5, sm: 0.75 },
                          px: { xs: 1, sm: 2 },
                          borderColor: '#4169E1',
                          color: '#4169E1',
                          '&:hover': {
                            borderColor: '#2952cc',
                            backgroundColor: 'rgba(65, 105, 225, 0.05)'
                          }
                        }}
                      >
                        تعديل
                      </Button>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setIsEditingAdminNotes(false);
                            setAdminNotesValue(selectedOrder.admin_notes || '');
                          }}
                          disabled={savingAdminNotes}
                          sx={{ 
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            py: { xs: 0.5, sm: 0.75 },
                            borderColor: '#6b7280',
                            color: '#6b7280'
                          }}
                        >
                          إلغاء
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleSaveAdminNotes}
                          disabled={savingAdminNotes}
                          sx={{ 
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            py: { xs: 0.5, sm: 0.75 },
                            backgroundColor: '#4169E1',
                            '&:hover': {
                              backgroundColor: '#2952cc'
                            }
                          }}
                        >
                          {savingAdminNotes ? 'جاري الحفظ...' : 'حفظ'}
                        </Button>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ 
                    p: { xs: 1.5, sm: 2 }, 
                    backgroundColor: isEditingAdminNotes ? '#f0f7ff' : '#fff9e6', 
                    borderRadius: 1, 
                    border: isEditingAdminNotes ? '2px solid #4169E1' : '1px solid #ffd54f',
                    minHeight: 80
                  }}>
                    {isEditingAdminNotes ? (
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={adminNotesValue}
                        onChange={(e) => setAdminNotesValue(e.target.value)}
                        placeholder="أضف ملاحظات داخلية للإدارة فقط..."
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            fontSize: { xs: '0.85rem', sm: '1rem' }
                          }
                        }}
                      />
                    ) : (
                      <Typography sx={{ 
                        whiteSpace: 'pre-wrap',
                        fontSize: { xs: '0.85rem', sm: '1rem' },
                        color: adminNotesValue ? 'inherit' : '#999',
                        fontStyle: adminNotesValue ? 'normal' : 'italic',
                        lineHeight: 1.6
                      }}>
                        {adminNotesValue || 'لا توجد ملاحظات إدارية'}
                      </Typography>
                    )}
                  </Box>
                </Paper>

                {/* Products Table */}
                {selectedOrder.items && selectedOrder.items.length > 0 && (
                  <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 3, backgroundColor: '#f8f9fa' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography 
                        variant={{ xs: 'h6', sm: 'h6' }} 
                        sx={{ 
                          color: '#10b981', 
                          fontWeight: 'bold',
                          fontSize: { xs: '1.1rem', sm: '1.25rem' }
                        }}
                      >
                        🛍️ المنتجات المطلوبة
                      </Typography>
                      {editingItems && (
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={handleOpenAddProductDialog}
                          sx={{
                            backgroundColor: '#10b981',
                            '&:hover': {
                              backgroundColor: '#059669'
                            },
                            fontSize: { xs: '0.8rem', sm: '0.9rem' },
                            px: { xs: 1.5, sm: 2 }
                          }}
                        >
                          إضافة منتج
                        </Button>
                      )}
                    </Box>
                    
                    <TableContainer 
                      sx={{ 
                        backgroundColor: 'white',
                        borderRadius: 2,
                        border: '1px solid #e0e0e0',
                        overflow: 'auto',
                        overflowX: 'auto'
                      }}
                    >
                      <Table>
                        <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151', width: '60px', textAlign: 'center' }}>
                              تم التجهيز
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151', width: '80px' }}>
                              الصورة
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>
                              اسم المنتج
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151', textAlign: 'center', width: editingItems ? '150px' : '100px' }}>
                              الكمية
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151', textAlign: 'center', width: { xs: '70px', sm: '80px' } }}>
                              إجراءات
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedOrder.items.map((item, index) => {
                            const isPrepared = preparedItems.has(item.id);
                            return (
                            <TableRow 
                              key={item.id || index}
                              sx={{
                                backgroundColor: isPrepared ? '#d4edda' : '#f8d7da',
                                '&:hover': {
                                  backgroundColor: isPrepared ? '#c3e6cb' : '#f5c6cb'
                                },
                                transition: 'background-color 0.3s ease'
                              }}
                            >
                              <TableCell sx={{ padding: '12px', textAlign: 'center' }}>
                                <Checkbox
                                  checked={isPrepared}
                                  onChange={() => handleToggleItemPrepared(item.id)}
                                  sx={{
                                    color: isPrepared ? '#28a745' : '#dc3545',
                                    '&.Mui-checked': {
                                      color: '#28a745',
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ padding: '12px' }}>
                                {item.product_image ? (
                                  <>
                                    <Box
                                      component="img"
                                      src={item.product_image.startsWith('http') ? 
                                        item.product_image : 
                                        `${API_BASE_URL}/api/uploads/products/${item.product_image}`
                                      }
                                      alt={item.product_name}
                                      sx={{ 
                                        width: 60, 
                                        height: 60, 
                                        borderRadius: 1, 
                                        objectFit: 'cover',
                                        border: '1px solid #e0e0e0',
                                        backgroundColor: '#f9f9f9'
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                      }}
                                    />
                                    <Box 
                                      sx={{ 
                                        width: 60, 
                                        height: 60, 
                                        borderRadius: 1, 
                                        border: '1px dashed #ccc',
                                        display: 'none',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#f9f9f9'
                                      }}
                                    >
                                      <ImageIcon sx={{ fontSize: 24, color: '#ccc' }} />
                                    </Box>
                                  </>
                                ) : (
                                  <Box 
                                    sx={{ 
                                      width: 60, 
                                      height: 60, 
                                      borderRadius: 1, 
                                      border: '1px dashed #ccc',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: '#f9f9f9'
                                    }}
                                  >
                                    <ImageIcon sx={{ fontSize: 24, color: '#ccc' }} />
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell sx={{ padding: '12px' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  {item.product_name}
                                </Typography>
                                {(item.product_sku || item.brand_name) && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                    {item.product_sku && `كود: ${item.product_sku}`}
                                    {item.product_sku && item.brand_name && ' | '}
                                    {item.brand_name && `البراند: ${item.brand_name}`}
                                  </Typography>
                                )}
                                {item.variant_info && (
                                  <Typography variant="caption" color="text.secondary">
                                    {item.variant_info}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ padding: '12px', textAlign: 'center' }}>
                                {editingItems ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleQuantityChange(item.id, -1)}
                                      disabled={item.quantity <= 1}
                                      sx={{ 
                                        width: { xs: 28, sm: 32 },
                                        height: { xs: 28, sm: 32 },
                                        backgroundColor: '#fee2e2',
                                        color: '#dc2626',
                                        '&:hover': { 
                                          backgroundColor: '#fecaca',
                                          transform: 'scale(1.1)'
                                        },
                                        '&.Mui-disabled': { 
                                          backgroundColor: '#f9fafb',
                                          color: '#d1d5db'
                                        },
                                        transition: 'all 0.2s'
                                      }}
                                    >
                                      <RemoveIcon sx={{ fontSize: { xs: '14px', sm: '16px' } }} />
                                    </IconButton>
                                    <Typography sx={{ 
                                      minWidth: { xs: '25px', sm: '35px' }, 
                                      textAlign: 'center', 
                                      fontWeight: 'bold',
                                      fontSize: { xs: '0.875rem', sm: '1rem' },
                                      backgroundColor: '#f3f4f6',
                                      borderRadius: 1,
                                      py: 0.5,
                                      px: 1
                                    }}>
                                      {item.quantity}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleQuantityChange(item.id, 1)}
                                      sx={{ 
                                        width: { xs: 28, sm: 32 },
                                        height: { xs: 28, sm: 32 },
                                        backgroundColor: '#dcfce7',
                                        color: '#16a34a',
                                        '&:hover': { 
                                          backgroundColor: '#bbf7d0',
                                          transform: 'scale(1.1)'
                                        },
                                        transition: 'all 0.2s'
                                      }}
                                    >
                                      <AddIcon sx={{ fontSize: { xs: '14px', sm: '16px' } }} />
                                    </IconButton>
                                  </Box>
                                ) : (
                                  <Chip 
                                    label={item.quantity} 
                                    size="small"
                                    sx={{ 
                                      backgroundColor: '#10b981',
                                      color: 'white',
                                      fontWeight: 'bold',
                                      minWidth: '40px',
                                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                    }}
                                  />
                                )}
                              </TableCell>
                              <TableCell sx={{ textAlign: 'center', padding: { xs: '6px', sm: '8px' } }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                                  {editingItems && (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteItem(item.id)}
                                      sx={{ 
                                        width: { xs: 32, sm: 36 },
                                        height: { xs: 32, sm: 36 },
                                        color: 'white',
                                        backgroundColor: '#ef4444',
                                        '&:hover': { 
                                          backgroundColor: '#dc2626',
                                          transform: 'scale(1.1)'
                                        },
                                        transition: 'all 0.2s',
                                        boxShadow: '0 2px 4px rgba(239, 68, 68, 0.3)'
                                      }}
                                      title="حذف المنتج"
                                    >
                                      <DeleteIcon sx={{ fontSize: { xs: '16px', sm: '18px' } }} />
                                    </IconButton>
                                  )}
                                  <IconButton
                                    size="small"
                                    onClick={() => handleMarkProductAsMissing(item, selectedOrder.id)}
                                    sx={{ 
                                      width: { xs: 32, sm: 36 },
                                      height: { xs: 32, sm: 36 },
                                      color: '#f59e0b',
                                      bgcolor: '#fef3c7',
                                      '&:hover': { 
                                        bgcolor: '#fde68a',
                                        transform: 'scale(1.1)'
                                      },
                                      transition: 'all 0.2s',
                                      boxShadow: '0 2px 4px rgba(245, 158, 11, 0.2)'
                                    }}
                                    title="إضافة إلى قائمة المفقودات"
                                  >
                                    <WarningIcon sx={{ fontSize: { xs: '16px', sm: '18px' } }} />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}

                {/* Total Price Section */}
                <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 3, backgroundColor: '#f8f9fa' }}>
                  <Box sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    backgroundColor: 'white', 
                    borderRadius: 2, 
                    border: '2px solid #10b981' 
                  }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#374151' }}>
                          المجموع الفرعي:
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sx={{ textAlign: 'left' }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#374151' }}>
                          {parseFloat(selectedOrder.subtotal || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} IQD
                        </Typography>
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#374151' }}>
                          رسوم التوصيل:
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sx={{ textAlign: 'left' }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#374151' }}>
                          {parseFloat(selectedOrder.delivery_fee || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} IQD
                        </Typography>
                      </Grid>

                      {selectedOrder.discount_amount > 0 && (
                        <>
                          <Grid item xs={6}>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#ef4444' }}>
                              الخصم {selectedOrder.coupon_code && `(${selectedOrder.coupon_code})`}:
                            </Typography>
                          </Grid>
                          <Grid item xs={6} sx={{ textAlign: 'left' }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#ef4444' }}>
                              -{parseFloat(selectedOrder.discount_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} IQD
                            </Typography>
                          </Grid>
                        </>
                      )}

                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#10b981' }}>
                          المجموع الكلي:
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sx={{ textAlign: 'left' }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#10b981' }}>
                          {(() => {
                            const subtotal = parseFloat(selectedOrder.subtotal || 0);
                            const delivery = parseFloat(selectedOrder.delivery_fee || 0);
                            const discount = parseFloat(selectedOrder.discount_amount || 0);
                            const finalTotal = subtotal + delivery - discount;
                            return finalTotal.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
                          })()} IQD
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Paper>
              </Box>
            )}
          </DialogContent>
          
          <DialogActions sx={{ 
            p: { xs: 2, sm: 3 }, 
            backgroundColor: '#f8f9fa', 
            gap: 1.5,
            flexDirection: { xs: 'column', sm: 'row' },
            '& .MuiButton-root': {
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: '140px' }
            }
          }}>
            {editingItems ? (
              <>
                <Button 
                  onClick={() => setEditingItems(false)} 
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  sx={{
                    borderColor: '#6b7280',
                    color: '#6b7280',
                    borderWidth: 2,
                    '&:hover': {
                      borderColor: '#4b5563',
                      backgroundColor: 'rgba(107, 114, 128, 0.1)',
                      borderWidth: 2
                    },
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    py: { xs: 1.2, sm: 1 }
                  }}
                >
                  إلغاء
                </Button>
                <Button 
                  onClick={handleSaveOrderChanges} 
                  variant="contained"
                  startIcon={<CheckIcon />}
                  sx={{
                    backgroundColor: '#10b981',
                    '&:hover': {
                      backgroundColor: '#059669',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
                    },
                    transition: 'all 0.2s',
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    py: { xs: 1.2, sm: 1 },
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                  }}
                >
                  حفظ التغييرات
                </Button>
              </>
            ) : (
              <>
                <Button 
                  onClick={() => setEditingItems(true)} 
                  variant="outlined"
                  startIcon={<EditIcon />}
                  sx={{
                    borderColor: '#10b981',
                    color: '#10b981',
                    borderWidth: 2,
                    '&:hover': {
                      borderColor: '#059669',
                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                      borderWidth: 2
                    },
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    py: { xs: 1.2, sm: 1 }
                  }}
                >
                  تعديل المنتجات
                </Button>
                
                {/* Shipping Integration Button */}
                {selectedOrder?.shipping_status !== 'sent' ? (
                  <Button
                    variant="contained"
                    startIcon={<LocalShippingIcon />}
                    onClick={() => handleOpenShippingDialog(selectedOrder)}
                    sx={{
                      backgroundColor: '#3b82f6',
                      '&:hover': { 
                        backgroundColor: '#2563eb',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)'
                      },
                      transition: 'all 0.2s',
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      py: { xs: 1.2, sm: 1 }
                    }}
                  >
                    إرسال للشحن
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<ReceiptIcon />}
                    onClick={() => handleViewShippingInfo(selectedOrder)}
                    sx={{
                      borderColor: '#10b981',
                      color: '#10b981',
                      '&:hover': { 
                        backgroundColor: 'rgba(16, 185, 129, 0.1)'
                      },
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      py: { xs: 1.2, sm: 1 }
                    }}
                  >
                    معلومات الشحن
                  </Button>
                )}
                
                {/* Sync Shipping Status Button */}
                {selectedOrder?.shipping_qr_id && (
                  <IconButton
                    onClick={() => handleSyncShippingStatus(selectedOrder.id)}
                    disabled={syncingShippingStatus}
                    sx={{ 
                      color: '#6366f1',
                      '&:hover': {
                        backgroundColor: 'rgba(99, 102, 241, 0.1)'
                      }
                    }}
                    title="تحديث حالة الشحن"
                  >
                    {syncingShippingStatus ? (
                      <CircularProgress size={20} />
                    ) : (
                      <RefreshIcon />
                    )}
                  </IconButton>
                )}
                
                <Button 
                  onClick={() => {
                    setDetailsDialogOpen(false);
                    setEditingItems(false);
                  }} 
                  variant="contained"
                  startIcon={<CloseIcon />}
                  sx={{
                    backgroundColor: '#10b981',
                    '&:hover': {
                      backgroundColor: '#059669',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
                    },
                    transition: 'all 0.2s',
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    py: { xs: 1.2, sm: 1 },
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                  }}
                >
                  إغلاق
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* Add Product Dialog */}
        <Dialog 
          open={addProductDialogOpen} 
          onClose={() => setAddProductDialogOpen(false)} 
          maxWidth="md" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            إضافة منتج إلى الطلب #{selectedOrder?.id}
          </DialogTitle>
          <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
            {/* Product Search */}
            <TextField
              fullWidth
              variant="outlined"
              placeholder="البحث عن منتج (الاسم، الكود، البراند)..."
              value={productSearchQuery}
              onChange={(e) => setProductSearchQuery(e.target.value)}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            {/* Products List */}
            {loadingProducts ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography>جاري البحث عن المنتجات...</Typography>
              </Box>
            ) : productSearchQuery.length < 2 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <SearchIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography color="text.secondary">
                  اكتب على الأقل حرفين للبحث عن المنتجات
                </Typography>
              </Box>
            ) : availableProducts.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">لا توجد منتجات مطابقة للبحث</Typography>
              </Box>
            ) : (
              <Box sx={{ maxHeight: 400, overflowY: 'auto', mb: 3 }}>
                {getFilteredProducts().map((product) => (
                  <Paper
                    key={product.id}
                    sx={{
                      p: 2,
                      mb: 2,
                      cursor: 'pointer',
                      border: selectedProduct?.id === product.id ? '2px solid #10b981' : '1px solid #e0e0e0',
                      backgroundColor: selectedProduct?.id === product.id ? '#f0fdf4' : 'white',
                      '&:hover': { backgroundColor: '#f8f9fa' }
                    }}
                    onClick={() => setSelectedProduct(product)}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={3} sm={2}>
                        {(product.image_url || product.image) ? (
                          <Box
                            component="img"
                            src={(product.image_url || product.image).startsWith('http') ? 
                              (product.image_url || product.image) : 
                              `${API_BASE_URL}/api/uploads/products/${product.image_url || product.image}`
                            }
                            alt={product.name}
                            sx={{ 
                              width: 60, 
                              height: 60, 
                              borderRadius: 1,
                              objectFit: 'cover'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <Box sx={{ width: 60, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f3f4f6', borderRadius: 1 }}>
                            <ImageIcon sx={{ fontSize: 30, color: '#9ca3af' }} />
                          </Box>
                        )}
                      </Grid>
                      <Grid item xs={5} sm={6}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                          {product.name}
                        </Typography>
                        {product.sku && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            كود: {product.sku}
                          </Typography>
                        )}
                        {product.brand_name && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            البراند: {product.brand_name}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={4} sm={4}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#10b981', fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                          {parseFloat(product.after_discount || product.sell_price || product.price || 0).toLocaleString()} IQD
                        </Typography>
                        {product.after_discount && (product.sell_price || product.price) && parseFloat(product.after_discount) < parseFloat(product.sell_price || product.price) && (
                          <Typography variant="caption" sx={{ display: 'block', textDecoration: 'line-through', color: 'text.secondary' }}>
                            {parseFloat(product.sell_price || product.price).toLocaleString()} IQD
                          </Typography>
                        )}
                        <Chip
                          label={(product.active === 1 || product.active === '1') ? 'متوفر' : 'غير متوفر'}
                          color={(product.active === 1 || product.active === '1') ? 'success' : 'error'}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Box>
            )}

            {/* Quantity Input */}
            {selectedProduct && (
              <Box sx={{ mt: 3, p: 3, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                  المنتج المحدد: {selectedProduct.name}
                </Typography>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="الكمية"
                      value={newProductQuantity}
                      onChange={(e) => setNewProductQuantity(parseInt(e.target.value) || 1)}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1">
                      <strong>إجمالي السعر:</strong> {(newProductQuantity * parseFloat(selectedProduct.after_discount || selectedProduct.sell_price || selectedProduct.price || 0)).toLocaleString()} IQD
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
            <Button onClick={() => setAddProductDialogOpen(false)} variant="outlined">
              إلغاء
            </Button>
            <Button 
              onClick={handleAddProductToOrder} 
              variant="contained"
              disabled={!selectedProduct}
              sx={{
                backgroundColor: '#10b981',
                '&:hover': {
                  backgroundColor: '#059669'
                }
              }}
            >
              إضافة إلى الطلب
            </Button>
          </DialogActions>
        </Dialog>

        {/* Missing Products Dialog */}
        <Dialog
          open={missingProductsDialogOpen}
          onClose={() => setMissingProductsDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ pb: 1, px: { xs: 2, sm: 3 } }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <InventoryIcon sx={{ color: '#f59e0b', fontSize: 32 }} />
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  المنتجات المفقودة
                </Typography>
                <Chip 
                  label={`${missingProducts.length} منتج`}
                  color="warning"
                  sx={{ fontWeight: 'bold' }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {missingProducts.length > 0 && (
                  <>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      onClick={exportMissingProductsToExcel}
                    >
                      تصدير Excel
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={handleClearMissingProducts}
                    >
                      مسح الكل
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
            {missingProducts.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 8,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2
              }}>
                <InventoryIcon sx={{ fontSize: 64, color: '#d1d5db' }} />
                <Typography variant="h6" color="text.secondary">
                  لا توجد منتجات مفقودة
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  قم بإضافة المنتجات المفقودة من تفاصيل الطلبات
                </Typography>
              </Box>
            ) : (
              <TableContainer 
                sx={{ 
                  backgroundColor: 'white',
                  borderRadius: 2,
                  border: '1px solid #e0e0e0',
                  overflow: 'hidden'
                }}
              >
                <Table>
                  <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151', width: '80px' }}>
                        الصورة
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>
                        اسم المنتج
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>
                        SKU
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>
                        العلامة التجارية
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151', textAlign: 'center' }}>
                        الكمية
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151', textAlign: 'center' }}>
                        رقم الطلب
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151', textAlign: 'center', width: '80px' }}>
                        إجراء
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {missingProducts.map((product) => (
                      <TableRow 
                        key={product.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: '#fef3c7'
                          }
                        }}
                      >
                        <TableCell sx={{ padding: '12px' }}>
                          {product.image ? (
                            <>
                              <Box
                                component="img"
                                src={product.image.startsWith('http') ? 
                                  product.image : 
                                  `${API_BASE_URL}/api/uploads/products/${product.image}`
                                }
                                alt={product.productName}
                                sx={{ 
                                  width: 60, 
                                  height: 60, 
                                  borderRadius: 1, 
                                  objectFit: 'cover',
                                  border: '1px solid #e0e0e0',
                                  backgroundColor: '#f9f9f9'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <Box 
                                sx={{ 
                                  width: 60, 
                                  height: 60, 
                                  borderRadius: 1, 
                                  border: '1px dashed #ccc',
                                  display: 'none',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: '#f9f9f9'
                                }}
                              >
                                <ImageIcon sx={{ fontSize: 24, color: '#ccc' }} />
                              </Box>
                            </>
                          ) : (
                            <Box 
                              sx={{ 
                                width: 60, 
                                height: 60, 
                                borderRadius: 1, 
                                border: '1px dashed #ccc',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#f9f9f9'
                              }}
                            >
                              <ImageIcon sx={{ fontSize: 24, color: '#ccc' }} />
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {product.productName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {product.sku || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {product.brandName || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Chip 
                            label={product.quantity} 
                            size="small"
                            color="warning"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Chip 
                            label={`#${product.orderId}`}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', padding: '12px' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveMissingProduct(product.id)}
                            sx={{ 
                              color: '#ef4444',
                              backgroundColor: 'rgba(239, 68, 68, 0.1)',
                              '&:hover': { 
                                backgroundColor: 'rgba(239, 68, 68, 0.2)' 
                              }
                            }}
                            title="إزالة من القائمة"
                          >
                            <DeleteIcon sx={{ fontSize: '18px' }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>

          <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
            <Button 
              onClick={() => setMissingProductsDialogOpen(false)} 
              variant="contained"
            >
              إغلاق
            </Button>
          </DialogActions>
        </Dialog>

        {/* WhatsApp Confirmation Dialog */}
        <Dialog 
          open={whatsappDialogOpen} 
          onClose={() => setWhatsappDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ 
            bgcolor: '#25D366', 
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <WhatsAppIcon />
            إرسال رسالة واتساب
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            {selectedOrderForWhatsApp && (
              <>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>رقم الطلب:</strong> {selectedOrderForWhatsApp.id}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>اسم العميل:</strong> {selectedOrderForWhatsApp.user_name}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>رقم الهاتف:</strong> {selectedOrderForWhatsApp.phone}
                </Typography>
                <Box sx={{ 
                  bgcolor: '#f5f5f5', 
                  p: 2, 
                  borderRadius: 1,
                  mt: 2
                }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                    الرسالة التي سيتم إرسالها:
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    السلام عليكم،{'\n'}
                    طلبك {selectedOrderForWhatsApp.id} في طريقه ويصيصل قريباً.{'\n'}
                    {'\n'}
                    التسليم التقديري: خلال 24 ساعة{'\n'}
                    {'\n'}
                    سوف يتواصل معك مندوب التوصيل قريباً.
                  </Typography>
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button 
              onClick={() => setWhatsappDialogOpen(false)}
              variant="outlined"
              disabled={sendingWhatsApp}
            >
              إلغاء
            </Button>
            <Button 
              onClick={handleSendWhatsAppMessage}
              variant="contained"
              disabled={sendingWhatsApp}
              startIcon={sendingWhatsApp ? <CircularProgress size={20} /> : <SendIcon />}
              sx={{ 
                bgcolor: '#25D366',
                '&:hover': {
                  bgcolor: '#1fae57'
                }
              }}
            >
              {sendingWhatsApp ? 'جاري الإرسال...' : 'إرسال الرسالة'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk WhatsApp Confirmation Dialog */}
        <Dialog 
          open={bulkWhatsAppDialogOpen} 
          onClose={() => setBulkWhatsAppDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ 
            bgcolor: '#25D366', 
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <WhatsAppIcon />
            إرسال رسائل واتساب جماعية
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography>
              هل أنت متأكد من إرسال رسائل واتساب إلى {selectedOrdersForExport.length} طلب؟
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button 
              onClick={() => setBulkWhatsAppDialogOpen(false)}
              variant="outlined"
              disabled={sendingWhatsApp}
            >
              إلغاء
            </Button>
            <Button 
              onClick={handleBulkWhatsAppSend}
              variant="contained"
              disabled={sendingWhatsApp}
              startIcon={sendingWhatsApp ? <CircularProgress size={20} /> : <SendIcon />}
              sx={{ 
                bgcolor: '#25D366',
                '&:hover': {
                  bgcolor: '#1fae57'
                }
              }}
            >
              {sendingWhatsApp ? 'جاري الإرسال...' : 'إرسال الرسائل'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Status Change Dialog */}
        <Dialog 
          open={bulkStatusDialogOpen} 
          onClose={() => !updatingBulkStatus && setBulkStatusDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ 
            bgcolor: '#f59e0b', 
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <EditIcon />
            تغيير حالة الطلبات
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography sx={{ mb: 2 }}>
              تغيير حالة {selectedOrdersForExport.length} طلب إلى:
            </Typography>
            <FormControl fullWidth>
              <InputLabel>اختر الحالة الجديدة</InputLabel>
              <Select
                value={selectedBulkStatus}
                onChange={(e) => setSelectedBulkStatus(e.target.value)}
                label="اختر الحالة الجديدة"
                disabled={updatingBulkStatus}
              >
                {statusOptions.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {status.icon}
                      <Typography>{status.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button 
              onClick={() => {
                setBulkStatusDialogOpen(false);
                setSelectedBulkStatus('');
              }}
              variant="outlined"
              disabled={updatingBulkStatus}
            >
              إلغاء
            </Button>
            <Button 
              onClick={handleBulkStatusChange}
              variant="contained"
              disabled={!selectedBulkStatus || updatingBulkStatus}
              startIcon={updatingBulkStatus ? <CircularProgress size={20} /> : <CheckIcon />}
              sx={{ 
                bgcolor: '#f59e0b',
                '&:hover': {
                  bgcolor: '#d97706'
                }
              }}
            >
              {updatingBulkStatus ? 'جاري التحديث...' : 'تحديث الحالة'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create Order Dialog */}
        <Dialog 
          open={createOrderDialogOpen} 
          onClose={() => setCreateOrderDialogOpen(false)}
          maxWidth="md" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            إنشاء طلب جديد
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="اسم العميل أو المصدر *"
                    value={newOrder.customer_name}
                    onChange={(e) => setNewOrder({ ...newOrder, customer_name: e.target.value })}
                    variant="outlined"
                    placeholder="مثال: أحمد محمد، Facebook، WhatsApp، Instagram"
                    helperText="أدخل اسم العميل أو مصدر الطلب"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="رقم الهاتف *"
                    value={newOrder.customer_phone}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
                      if (value.length <= 11) {
                        setNewOrder({ ...newOrder, customer_phone: value });
                        if (value.length === 11 && validatePhoneNumber(value)) {
                          setPhoneError('');
                        } else if (value.length > 0) {
                          setPhoneError('رقم الهاتف يجب أن يبدأ بـ 07 ويتكون من 11 رقم');
                        } else {
                          setPhoneError('');
                        }
                      }
                    }}
                    variant="outlined"
                    placeholder="07XXXXXXXXX"
                    error={!!phoneError}
                    helperText={phoneError || 'مثال: 07712345678'}
                    inputProps={{
                      maxLength: 11,
                      inputMode: 'numeric',
                      pattern: '[0-9]*'
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="المدينة *"
                    value={newOrder.city}
                    onChange={(e) => {
                      setNewOrder({ ...newOrder, city: e.target.value });
                      setShowCustomCity(e.target.value === 'أخرى');
                      if (e.target.value !== 'أخرى') {
                        setCustomCity('');
                      }
                    }}
                    variant="outlined"
                    SelectProps={{
                      native: true
                    }}
                  >
                    <option value="">اختر المدينة</option>
                    {cities.map((city) => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                    <option value="أخرى">أخرى</option>
                  </TextField>
                </Grid>
                {showCustomCity && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="اسم المدينة *"
                      value={customCity}
                      onChange={(e) => setCustomCity(e.target.value)}
                      variant="outlined"
                      placeholder="أدخل اسم المدينة"
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="العنوان الكامل *"
                    value={newOrder.shipping_address}
                    onChange={(e) => setNewOrder({ ...newOrder, shipping_address: e.target.value })}
                    variant="outlined"
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="حالة الطلب"
                    value={newOrder.status}
                    onChange={(e) => setNewOrder({ ...newOrder, status: e.target.value })}
                    variant="outlined"
                    SelectProps={{
                      native: true
                    }}
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="ملاحظات العميل"
                    value={newOrder.notes}
                    onChange={(e) => setNewOrder({ ...newOrder, notes: e.target.value })}
                    variant="outlined"
                    multiline
                    rows={3}
                    placeholder="أي ملاحظات من العميل..."
                  />
                </Grid>
              </Grid>
              <Alert severity="info" sx={{ mt: 2 }}>
                بعد إنشاء الطلب، يمكنك إضافة المنتجات من صفحة تفاصيل الطلب
              </Alert>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={() => setCreateOrderDialogOpen(false)} 
              variant="outlined"
            >
              إلغاء
            </Button>
            <Button 
              onClick={handleCreateOrder} 
              variant="contained"
              disabled={creatingOrder}
              sx={{ backgroundColor: '#8b5cf6', '&:hover': { backgroundColor: '#7c3aed' } }}
            >
              {creatingOrder ? 'جاري الإنشاء...' : 'إنشاء الطلب'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Shipping Integration Dialog */}
        <Dialog
          open={shippingDialogOpen}
          onClose={() => setShippingDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ bgcolor: '#3b82f6', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalShippingIcon />
              <Typography variant="h6">
                إرسال الطلب #{selectedOrder?.id} إلى شركة الشحن
              </Typography>
            </Box>
          </DialogTitle>
          
          <DialogContent sx={{ pt: 3 }}>
            {loadingShippingData ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={2}>
                {/* City Selection */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>المدينة *</InputLabel>
                    <Select
                      value={selectedShippingCity}
                      onChange={(e) => handleShippingCityChange(e.target.value)}
                      label="المدينة *"
                    >
                      <MenuItem value="">اختر المدينة</MenuItem>
                      {shippingCities.map(city => (
                        <MenuItem key={city.id} value={city.id}>
                          {city.city_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Region Selection */}
                <Grid item xs={12}>
                  <FormControl fullWidth disabled={!selectedShippingCity}>
                    <InputLabel>المنطقة *</InputLabel>
                    <Select
                      value={selectedShippingRegion}
                      onChange={(e) => setSelectedShippingRegion(e.target.value)}
                      label="المنطقة *"
                    >
                      <MenuItem value="">اختر المنطقة</MenuItem>
                      {shippingRegions.map(region => (
                        <MenuItem key={region.id} value={region.id}>
                          {region.region_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {!selectedShippingCity && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      اختر المدينة أولاً
                    </Typography>
                  )}
                </Grid>
                
                {/* Package Size Selection */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>حجم الطرد *</InputLabel>
                    <Select
                      value={selectedPackageSize}
                      onChange={(e) => setSelectedPackageSize(e.target.value)}
                      label="حجم الطرد *"
                    >
                      <MenuItem value="">اختر الحجم</MenuItem>
                      {packageSizes.map(size => (
                        <MenuItem key={size.id} value={size.id}>
                          {size.size}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                {/* Order Info */}
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      معلومات الطلب:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="body2">
                        • العميل: <strong>{selectedOrder?.customer_name}</strong>
                      </Typography>
                      <Typography variant="body2">
                        • الهاتف: <strong>{selectedOrder?.customer_phone}</strong>
                      </Typography>
                      <Typography variant="body2">
                        • العنوان: <strong>{selectedOrder?.shipping_address}</strong>
                      </Typography>
                      <Typography variant="body2">
                        • المبلغ الكلي: <strong>{selectedOrder?.total_amount?.toLocaleString()} IQD</strong>
                      </Typography>
                      <Typography variant="body2">
                        • عدد المنتجات: <strong>{selectedOrder?.items?.length || 0}</strong>
                      </Typography>
                    </Box>
                  </Alert>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button 
              onClick={() => setShippingDialogOpen(false)}
              variant="outlined"
              disabled={sendingToShipping}
            >
              إلغاء
            </Button>
            <Button
              onClick={handleSendToShipping}
              variant="contained"
              disabled={!selectedShippingCity || !selectedShippingRegion || !selectedPackageSize || sendingToShipping}
              startIcon={sendingToShipping ? <CircularProgress size={20} /> : <SendIcon />}
              sx={{ 
                bgcolor: '#3b82f6',
                '&:hover': { bgcolor: '#2563eb' }
              }}
            >
              {sendingToShipping ? 'جاري الإرسال...' : 'إرسال للشحن'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Shipping Company Orders Dialog */}
        <Dialog
          open={shippingCompanyOrdersDialogOpen}
          onClose={() => setShippingCompanyOrdersDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ bgcolor: '#10b981', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalShippingIcon />
              <Typography variant="h6">
                Orders on Shipping Company Website
              </Typography>
            </Box>
          </DialogTitle>
          
          <DialogContent sx={{ pt: 3 }}>
            {loadingShippingOrders ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress />
              </Box>
            ) : shippingCompanyOrders.length === 0 ? (
              <Alert severity="info">
                No orders found on shipping company website
              </Alert>
            ) : (
              <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Order ID</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Tracking #</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Customer</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Phone</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>City</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Region</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Price</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Created</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {shippingCompanyOrders.map((order, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{order.id}</TableCell>
                        <TableCell>
                          <Chip 
                            label={order.qr_id || 'N/A'} 
                            size="small" 
                            color="primary"
                            sx={{ fontFamily: 'monospace' }}
                          />
                        </TableCell>
                        <TableCell>{order.client_name}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {order.client_mobile}
                            {order.client_mobile2 && (
                              <Chip label={order.client_mobile2} size="small" variant="outlined" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{order.city_name}</TableCell>
                        <TableCell>{order.region_name}</TableCell>
                        <TableCell>
                          <Chip 
                            label={order.status} 
                            size="small"
                            color={
                              order.status_id === '2' ? 'success' : 
                              order.status_id === '1' ? 'warning' : 
                              'default'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {parseFloat(order.price || 0).toLocaleString()} IQD
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {new Date(order.created_at).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const info = `
Order Details:
━━━━━━━━━━━━━━━━━━━━
Tracking #: ${order.qr_id || order.id}
Customer: ${order.client_name}
Phone: ${order.client_mobile}
Location: ${order.city_name} - ${order.region_name}
Address: ${order.location}
Price: ${order.price} IQD
Items: ${order.items_number}
Package Size: ${order.package_size}
Status: ${order.status}
Merchant Notes: ${order.merchant_notes || 'None'}
Issue Notes: ${order.issue_notes || 'None'}
Created: ${order.created_at}
Updated: ${order.updated_at}
                                `.trim();
                                alert(info);
                              }}
                              sx={{ color: '#3b82f6' }}
                              title="View Details"
                            >
                              <ViewIcon sx={{ fontSize: '18px' }} />
                            </IconButton>
                            {order.qr_link && (
                              <IconButton
                                size="small"
                                onClick={() => window.open(order.qr_link, '_blank')}
                                sx={{ color: '#10b981' }}
                                title="Print Label"
                              >
                                <ReceiptIcon sx={{ fontSize: '18px' }} />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            
            {!loadingShippingOrders && shippingCompanyOrders.length > 0 && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <strong>Total Orders on Shipping Company:</strong> {shippingCompanyOrders.length}
              </Alert>
            )}
          </DialogContent>
          
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={() => setShippingCompanyOrdersDialogOpen(false)}
              variant="contained"
              sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </ThemeProvider>
  );
};

export default FullControl;